/**
 * i18n - Internationalization System
 * Supports: Thai (th), English (en), Myanmar (mm)
 */

import { useCallback } from 'react';
import { useAppSettings } from '@/hooks/useAppSettings';

export type Language = 'th' | 'en' | 'mm';

export const translations: Record<Language, Record<string, string>> = {
  th: {
    'app.title': 'GreenHouse Pro',
    'app.subtitle': 'ระบบจัดการโรงเรือนอัจฉริยะ',

    'project.locked': 'ล็อก',
    'project.viewGreenhouses': 'เข้าดูโรงเรือน',
    'project.noAccess': 'คุณไม่มีสิทธิ์เข้าถึงโปรเจกต์นี้',

    'nav.home': 'หน้าแรก',
    'nav.agriculture': 'การเกษตร',
    'nav.dashboard': 'แดชบอร์ด',
    'nav.projects': 'โปรเจกต์',
    'nav.greenhouses': 'โรงเรือน',
    'nav.admin': 'ผู้ดูแลระบบ',
    'nav.profile': 'โปรไฟล์',
    'nav.settings': 'ตั้งค่า',
    'nav.logout': 'ออกจากระบบ',
    'nav.notifications': 'แจ้งเตือน',

    'auth.login': 'เข้าสู่ระบบ',
    'auth.logout': 'ออกจากระบบ',
    'auth.username': 'ชื่อผู้ใช้',
    'auth.password': 'รหัสผ่าน',
    'auth.remember': 'จดจำฉัน',
    'auth.forgot': 'ลืมรหัสผ่าน?',

    'common.save': 'บันทึก',
    'common.cancel': 'ยกเลิก',
    'common.delete': 'ลบ',
    'common.edit': 'แก้ไข',
    'common.add': 'เพิ่ม',
    'common.search': 'ค้นหา',
    'common.filter': 'กรอง',
    'common.refresh': 'รีเฟรช',
    'common.loading': 'กำลังโหลด...',
    'common.noData': 'ไม่มีข้อมูล',
    'common.confirm': 'ยืนยัน',
    'common.back': 'กลับ',
    'common.next': 'ถัดไป',
    'common.previous': 'ก่อนหน้า',
    'common.close': 'ปิด',
    'common.export': 'ส่งออก',
    'common.import': 'นำเข้า',
    'common.download': 'ดาวน์โหลด',
    'common.upload': 'อัพโหลด',
    'common.all': 'ทั้งหมด',
    'common.active': 'ใช้งาน',
    'common.inactive': 'ไม่ใช้งาน',
    'common.status': 'สถานะ',
    'common.actions': 'จัดการ',
    'common.details': 'รายละเอียด',
    'common.date': 'วันที่',
    'common.time': 'เวลา',
    'common.yes': 'ใช่',
    'common.no': 'ไม่',
    'common.retry': 'ลองใหม่',
    'common.error': 'เกิดข้อผิดพลาด',

    'page.selectProject': 'เลือกโปรเจกต์',
    'page.selectProjectDesc': 'เลือกโปรเจกต์ที่ต้องการจัดการโรงเรือน',
    'page.noProject': 'ไม่พบโปรเจกต์',
    'page.noProjectDesc': 'คุณยังไม่มีสิทธิ์เข้าถึงโปรเจกต์ใดๆ',
    'page.noGreenhouse': 'ไม่พบโรงเรือน',
    'page.noGreenhouseDesc': 'โปรเจกต์นี้ยังไม่มีโรงเรือน',
    'page.loadingGreenhouse': 'กำลังโหลดข้อมูลโรงเรือน...',
    'page.online': 'ออนไลน์',
    'page.offline': 'ออฟไลน์',
    'page.ready': 'พร้อมใช้งาน',
    'page.developing': 'กำลังพัฒนา',
    'page.greenhouse': 'โรงเรือน',
    'page.loading': 'กำลังโหลด...',

    'greenhouse.ready': 'พร้อมใช้งาน',
    'greenhouse.online': 'ออนไลน์',
    'greenhouse.offline': 'ออฟไลน์',
    'greenhouse.checking': 'กำลังตรวจสอบ',
    'greenhouse.developing': 'กำลังพัฒนา',
    'greenhouse.noDevice': 'ยังไม่มี Device ID',

    'dashboard.soilTab': 'ค่าดิน',
    'dashboard.chartsTab': 'กราฟ',
    'dashboard.controlTab': 'ควบคุม',
    'dashboard.timersTab': 'ตั้งเวลา',
    'dashboard.temperature': 'อุณหภูมิ',
    'dashboard.humidity': 'ความชื้น',
    'dashboard.moisture': 'ความชื้นดิน',
    'dashboard.co2': 'CO₂',
    'dashboard.light': 'แสง',
    'dashboard.online': 'ออนไลน์',
    'dashboard.offline': 'ออฟไลน์',
    'dashboard.lastUpdate': 'อัพเดทล่าสุด',
    'dashboard.autoTab': 'ระบบทำงาน Auto',
    'dashboard.cmdSent': 'ส่งคำสั่งแล้ว',
    'dashboard.motorCmdSent': 'ส่งคำสั่งมอเตอร์แล้ว',
    'dashboard.relayCmdFailed': 'ส่งคำสั่ง {name} ไม่สำเร็จ กรุณาลองใหม่',
    'dashboard.motorCmdFailed': 'ส่งคำสั่ง {name} ไม่สำเร็จ กรุณาลองใหม่',
    'dashboard.noControlPermission': 'คุณไม่มีสิทธิ์ควบคุมอุปกรณ์ (ต้องเป็น superadmin / operator / admin)',
    'dashboard.autoModeTitle': 'เปิดใช้งานโหมดทำงานตามเวลาอัตโนมัติ',
    'dashboard.relayTitle': 'ควบคุมรีเลย์',
    'dashboard.motorTitle': 'ระบบพรางแสง',
    'dashboard.shadeAll': 'พรางแสงทั้ง 4 โซน',
    'dashboard.unshadeAll': 'ไม่พรางแสงทั้ง 4 โซน',

    'sensor.air': 'อากาศ',
    'sensor.soil': 'ดิน',
    'sensor.water': 'น้ำ',
    'sensor.light': 'แสง',
    'sensor.npk': 'ธาตุอาหาร NPK',
    'sensor.ec': 'ค่า EC',
    'sensor.ph': 'ค่า pH',

    'control.on': 'เปิด',
    'control.off': 'ปิด',
    'control.auto': 'อัตโนมัติ',
    'control.manual': 'ควบคุมเอง',
    'control.fan': 'พัดลม',
    'control.pump': 'ปั๊มน้ำ',
    'control.valve': 'วาล์ว',
    'control.light': 'ไฟ',
    'control.motor': 'มอเตอร์',
    'control.forward': 'เดินหน้า',
    'control.reverse': 'ถอยหลัง',
    'control.stop': 'หยุด',

    'admin.users': 'จัดการผู้ใช้',
    'admin.projects': 'จัดการโปรเจกต์',
    'admin.greenhouses': 'จัดการโรงเรือน',
    'admin.sensors': 'จัดการ Sensor',
    'admin.controls': 'จัดการอุปกรณ์ควบคุม',
    'admin.notifications': 'การแจ้งเตือน',
    'admin.alerts': 'ประวัติ Alert',
    'admin.audit': 'Audit Log',
    'admin.settings': 'ตั้งค่าระบบ',
    'admin.security': 'ความปลอดภัย',
    'admin.panel': 'Admin Panel',
    'admin.nav.system': 'ระบบ',
    'admin.nav.control': 'ควบคุม',
    'admin.nav.notify': 'แจ้งเตือน',
    'admin.nav.settings': 'ตั้งค่า',
    'admin.nav.backToApp': 'กลับหน้าหลัก',
    'admin.nav.automation': 'Automation Rules',
    'admin.nav.scenes': 'Scenes / Presets',
    'admin.nav.controlHistory': 'ประวัติการควบคุม',


    'admin.user.subtitle': 'เพิ่ม แก้ไข และจัดการสิทธิ์ผู้ใช้งานระบบ',
    'admin.user.searchPlaceholder': 'ค้นหาผู้ใช้...',
    'admin.user.addUser': 'เพิ่มผู้ใช้',
    'admin.user.colUser': 'ผู้ใช้',
    'admin.user.colRole': 'สิทธิ์',
    'admin.user.colProject': 'โปรเจกต์',
    'admin.user.allProjects': 'ทั้งหมด',
    'admin.user.projectCount': '{n} โปรเจกต์',
    'admin.user.active': 'เปิดใช้งาน',
    'admin.user.inactive': 'ปิดใช้งาน',
    'admin.user.roleAdmin': 'ผู้ดูแล',
    'admin.user.roleOperator': 'ผู้ควบคุม',
    'admin.user.roleViewer': 'ผู้ชม',
    'admin.user.noUser': 'ไม่พบผู้ใช้',
    'admin.user.createTitle': 'เพิ่มผู้ใช้ใหม่',
    'admin.user.editTitle': 'แก้ไขผู้ใช้',
    'admin.user.resetTitle': 'รีเซ็ตรหัสผ่าน',
    'admin.user.fieldUsername': 'ชื่อผู้ใช้',
    'admin.user.fieldUsernameHint': 'ใช้สำหรับเข้าสู่ระบบ (อย่างน้อย 3 ตัวอักษร)',
    'admin.user.fieldUsernamePlaceholder': 'เช่น somchai',
    'admin.user.fieldEmail': 'อีเมล (ไม่บังคับ)',
    'admin.user.fieldEmailHint': 'ใช้สำหรับติดต่อ/กู้รหัสผ่าน (ปล่อยว่างได้)',
    'admin.user.fieldEmailPlaceholder': 'name@example.com',
    'admin.user.fieldPassword': 'รหัสผ่าน',
    'admin.user.fieldPasswordHint': 'แนะนำ: ผสมตัวอักษรและตัวเลขเพื่อความปลอดภัย',
    'admin.user.fieldPasswordPlaceholder': 'อย่างน้อย 6 ตัวอักษร',
    'admin.user.fieldRole': 'สิทธิ์',
    'admin.user.roleHint': 'Viewer = ดูอย่างเดียว, Operator = ควบคุม/ตั้งเวลาได้, Admin = จัดการผู้ใช้/โปรเจกต์ได้',
    'admin.user.createSuccess': 'สร้างผู้ใช้สำเร็จ',
    'admin.user.deleteConfirm': 'ต้องการลบผู้ใช้ "{name}" หรือไม่?',
    'admin.user.deleteSuccess': 'ลบผู้ใช้สำเร็จ',
    'admin.user.noDeleteSuper': 'ไม่อนุญาตให้ลบ Super Admin',
    'admin.user.noToggleSuper': 'ไม่อนุญาตให้ปิด/เปิด Super Admin',
    'admin.user.noEditSuper': 'ไม่อนุญาตให้ Admin แก้ไข Super Admin',
    'admin.user.superLockRole': 'ผู้ใช้เป็น Super Admin (ล็อกการเปลี่ยน Role) — แก้ได้เฉพาะชื่อผู้ใช้และอีเมล',
    'admin.user.activatedMsg': 'เปิดใช้งานผู้ใช้แล้ว',
    'admin.user.deactivatedMsg': 'ปิดใช้งานผู้ใช้แล้ว',
    'admin.user.noChange': 'ไม่มีการเปลี่ยนแปลง',
    'admin.user.saveSuccess': 'บันทึกข้อมูลสำเร็จ',
    'admin.user.accessTitle': 'สิทธิ์โปรเจกต์',
    'admin.user.accessDesc': 'เลือกโปรเจกต์ที่ผู้ใช้สามารถเข้าถึงได้',
    'admin.user.accessAll': 'ผู้ใช้ role = {role} เข้าถึงได้ "ทั้งหมด" (ไม่จำเป็นต้องกำหนดรายโปรเจกต์)',
    'admin.user.accessLoading': 'กำลังโหลดสิทธิ์...',
    'admin.user.accessSaveSuccess': 'บันทึกสิทธิ์สำเร็จ',
    'admin.user.accessLoadError': 'โหลดสิทธิ์โปรเจกต์ไม่สำเร็จ',
    'admin.user.superAllAccess': 'Super Admin เข้าถึงทั้งหมด',
    'admin.user.adminAllAccess': 'Admin เข้าถึงทั้งหมดอยู่แล้ว',
    'admin.user.manageAccess': 'จัดการสิทธิ์โปรเจกต์',
    'admin.user.onlySuperReset': 'เฉพาะ Super Admin เท่านั้นที่รีเซ็ตรหัสผ่านได้',
    'admin.user.resetPassword': 'รีเซ็ตรหัสผ่าน',
    'admin.user.resetFor': 'ผู้ใช้',
    'admin.user.newPassword': 'รหัสผ่านใหม่',
    'admin.user.newPasswordPlaceholder': 'อย่างน้อย 6 ตัวอักษร',
    'admin.user.newPasswordHint': 'แนะนำ: ใช้ตัวอักษร + ตัวเลข เพื่อความปลอดภัย',
    'admin.user.confirmPassword': 'ยืนยันรหัสผ่านใหม่',
    'admin.user.confirmPasswordPlaceholder': 'กรอกซ้ำอีกครั้ง',
    'admin.user.showPassword': 'แสดงรหัสผ่าน',
    'admin.user.hidePassword': 'ซ่อนรหัสผ่าน',
    'admin.user.resetSuccess': 'รีเซ็ตรหัสผ่านของ {name} สำเร็จ',
    'admin.user.resetBtn': 'รีเซ็ต',

    'admin.project.subtitle': 'เพิ่ม แก้ไข และตั้งค่าโปรเจกต์',
    'admin.project.searchPlaceholder': 'ค้นหาโปรเจกต์...',
    'admin.project.addProject': 'เพิ่มโปรเจกต์',
    'admin.project.greenhouse': 'โรงเรือน',
    'admin.project.deleteConfirm': 'ต้องการลบโปรเจกต์ "{name}" หรือไม่?',
    'admin.project.deleteSuccess': 'ลบโปรเจกต์สำเร็จ',
    'admin.project.createSuccess': 'สร้างโปรเจกต์สำเร็จ',
    'admin.project.saveSuccess': 'บันทึกโปรเจกต์สำเร็จ',
    'admin.project.createTitle': 'เพิ่มโปรเจกต์ใหม่',
    'admin.project.editTitle': 'แก้ไขโปรเจกต์',
    'admin.project.fieldKey': 'Key',
    'admin.project.fieldName': 'ชื่อโปรเจกต์',
    'admin.project.fieldStatus': 'สถานะ',
    'admin.project.statusDeveloping': 'กำลังพัฒนา',
    'admin.project.statusReady': 'พร้อมใช้งาน',
    'admin.project.tbSection': 'ThingsBoard',
    'admin.project.tbUrl': 'URL',
    'admin.project.tbUrlPlaceholder': 'http://thingsboard:8080',
    'admin.project.tbUsername': 'Username',
    'admin.project.tbPassword': 'Password',
    'admin.project.tbPasswordPlaceholder': '(ไม่เปลี่ยน)',
    'admin.project.noProject': 'ไม่พบโปรเจกต์',
    'admin.project.createBtn': 'สร้าง',

    'admin.gh.subtitle': 'เพิ่ม แก้ไข และเชื่อมต่ออุปกรณ์ ThingsBoard',
    'admin.gh.searchPlaceholder': 'ค้นหาโรงเรือน...',
    'admin.gh.allProjects': 'ทุกโปรเจกต์',
    'admin.gh.addGreenhouse': 'เพิ่มโรงเรือน',
    'admin.gh.colGreenhouse': 'โรงเรือน',
    'admin.gh.colProject': 'โปรเจกต์',
    'admin.gh.colDeviceId': 'Device ID',
    'admin.gh.notConnected': 'ยังไม่เชื่อมต่อ',
    'admin.gh.online': 'ออนไลน์',
    'admin.gh.offline': 'ออฟไลน์',
    'admin.gh.statusReady': 'พร้อม',
    'admin.gh.statusDeveloping': 'กำลังพัฒนา',
    'admin.gh.noGreenhouse': 'ไม่พบโรงเรือน',
    'admin.gh.deleteConfirm': 'ต้องการลบโรงเรือน "{name}" หรือไม่?',
    'admin.gh.deleteSuccess': 'ลบโรงเรือนสำเร็จ',
    'admin.gh.unlinkConfirm': 'ต้องการยกเลิกการเชื่อมต่ออุปกรณ์หรือไม่?',
    'admin.gh.unlinkSuccess': 'ยกเลิกการเชื่อมต่อสำเร็จ',
    'admin.gh.unlinkTitle': 'ยกเลิกการเชื่อมต่อ',
    'admin.gh.linkTitle': 'เชื่อมต่ออุปกรณ์',
    'admin.gh.createTitle': 'เพิ่มโรงเรือนใหม่',
    'admin.gh.createSuccess': 'สร้างโรงเรือนสำเร็จ',
    'admin.gh.fieldProject': 'โปรเจกต์',
    'admin.gh.fieldKey': 'Key (เช่น greenhouse1)',
    'admin.gh.fieldName': 'ชื่อโรงเรือน',
    'admin.gh.fieldDeviceId': 'ThingsBoard Device ID (ถ้ามี)',
    'admin.gh.fieldDeviceIdOptional': 'ไม่บังคับ',
    'admin.gh.createBtn': 'สร้าง',
    'admin.gh.linkDeviceTitle': 'เชื่อมต่ออุปกรณ์',
    'admin.gh.linkDeviceHint': 'คัดลอก Device ID จาก ThingsBoard (Devices → Device Details → Copy device id)',
    'admin.gh.linkSuccess': 'เชื่อมต่ออุปกรณ์สำเร็จ',
    'admin.gh.linkBtn': 'เชื่อมต่อ',

    'admin.sensor.subtitle': 'เพิ่ม แก้ไข Sensor แบบ Dynamic รองรับการขยายในอนาคต',
    'admin.sensor.selectProject': 'เลือกโปรเจกต์...',
    'admin.sensor.selectProjectHint': 'เลือกโปรเจกต์ที่ต้องการจัดการ',
    'admin.sensor.selectGh': 'เลือกโรงเรือน...',
    'admin.sensor.selectGhHint': 'เลือกโรงเรือนที่ต้องการตั้งค่า Sensor',
    'admin.sensor.pleaseSelect': 'กรุณาเลือกโปรเจกต์และโรงเรือน',
    'admin.sensor.noSensor': 'ไม่มี Sensor - กด Template เพื่อสร้างชุดมาตรฐาน',
    'admin.sensor.colType': 'ประเภท',
    'admin.sensor.colUnit': 'หน่วย',
    'admin.sensor.colAlert': 'แจ้งเตือน',
    'admin.sensor.deleteConfirm': 'ลบ "{name}"?',
    'admin.sensor.deleteSuccess': 'ลบสำเร็จ',
    'admin.sensor.saveSuccess': 'บันทึกสำเร็จ',
    'admin.sensor.createTitle': 'เพิ่ม Sensor',
    'admin.sensor.editTitle': 'แก้ไข Sensor',
    'admin.sensor.fieldKey': 'Sensor Key (ภาษาอังกฤษ)',
    'admin.sensor.fieldKeyHint': 'รหัสเฉพาะ เช่น air_temp, soil1_moisture ใช้ตัวพิมพ์เล็ก ขีดล่าง และตัวเลข',
    'admin.sensor.fieldName': 'ชื่อ Sensor (ภาษาไทย)',
    'admin.sensor.fieldNameHint': 'ชื่อที่แสดงบนหน้าจอ เช่น อุณหภูมิอากาศ',
    'admin.sensor.fieldType': 'ประเภท',
    'admin.sensor.fieldTypeHint': 'จัดกลุ่มการแสดงผล',
    'admin.sensor.fieldUnit': 'หน่วย',
    'admin.sensor.fieldUnitHint': 'หน่วยวัด',
    'admin.sensor.fieldUnitPlaceholder': '°C, %, ppm',
    'admin.sensor.fieldDataKey': 'Data Key (ThingsBoard)',
    'admin.sensor.fieldDataKeyHint': 'ชื่อ Telemetry Key จาก ThingsBoard ต้องตรงกับที่ Device ส่งมา',
    'admin.sensor.alertMin': 'แจ้งเตือนต่ำกว่า',
    'admin.sensor.alertMinHint': 'ว่างไว้ = ไม่แจ้งเตือน',
    'admin.sensor.alertMax': 'แจ้งเตือนสูงกว่า',
    'admin.sensor.alertMaxHint': 'ว่างไว้ = ไม่แจ้งเตือน',
    'admin.sensor.minValue': 'ขอบเขตต่ำสุด (gauge)',
    'admin.sensor.minValueHint': 'ว่างไว้ = ใช้ค่า default ของหน้า Dashboard',
    'admin.sensor.maxValue': 'ขอบเขตสูงสุด (gauge)',
    'admin.sensor.maxValueHint': 'ว่างไว้ = ใช้ค่า default ของหน้า Dashboard',
    'admin.sensor.calibration': 'Calibration',
    'admin.sensor.calibOffset': 'Offset (+/-)',
    'admin.sensor.calibOffsetHint': 'ค่าที่บวก/ลบจากค่าจริง',
    'admin.sensor.calibScale': 'Scale (x)',
    'admin.sensor.calibScaleHint': 'ตัวคูณ (1 = ไม่เปลี่ยน)',
    'admin.sensor.templateTitle': 'เลือก Template',
    'admin.sensor.templateDesc': 'สร้างชุด Sensor อัตโนมัติ (ข้ามรายการที่มีอยู่แล้ว)',
    'admin.sensor.templateSuccess': 'สร้าง Sensor สำเร็จ',
    'admin.sensor.typeAir': 'อากาศ',
    'admin.sensor.typeSoil': 'ดิน',
    'admin.sensor.typeWater': 'น้ำ',
    'admin.sensor.typeLight': 'แสง',
    'admin.sensor.typeCustom': 'อื่นๆ',

    'admin.notif.subtitle': 'ตั้งค่า Line Notify และเงื่อนไขการแจ้งเตือน',
    'admin.notif.lineNotify': 'Line Notify',
    'admin.notif.lineNotifyDesc': 'รับการแจ้งเตือนผ่าน Line เมื่อมีเหตุการณ์สำคัญ',
    'admin.notif.tokenSection': 'Token การเชื่อมต่อ',
    'admin.notif.currentToken': 'Token ปัจจุบัน:',
    'admin.notif.newToken': 'Line Notify Token ใหม่',
    'admin.notif.tokenUnchanged': '(ไม่เปลี่ยนแปลง)',
    'admin.notif.tokenPlaceholder': 'วาง Token ที่นี่',
    'admin.notif.tokenHowTo': 'วิธีรับ Token:',
    'admin.notif.tokenStep1': '1. ไปที่ notify-bot.line.me',
    'admin.notif.tokenStep2': '2. Login ด้วย Line Account',
    'admin.notif.tokenStep3': '3. คลิก "Generate Token"',
    'admin.notif.tokenStep4': '4. เลือกกลุ่มหรือ 1-on-1 ที่ต้องการรับแจ้งเตือน',
    'admin.notif.tokenStep5': '5. คัดลอก Token มาวางที่นี่',
    'admin.notif.alertTypes': 'ประเภทการแจ้งเตือน',
    'admin.notif.alertOffline': 'แจ้งเตือนเมื่ออุปกรณ์ออฟไลน์',
    'admin.notif.alertOfflineDesc': 'ส่งข้อความเมื่ออุปกรณ์ขาดการเชื่อมต่อ',
    'admin.notif.alertThreshold': 'แจ้งเตือนเมื่อค่าเกินเกณฑ์',
    'admin.notif.alertThresholdDesc': 'ส่งข้อความเมื่อค่า sensor ผิดปกติ',
    'admin.notif.thresholds': 'เกณฑ์การแจ้งเตือน',
    'admin.notif.tempLabel': 'อุณหภูมิอากาศ (°C)',
    'admin.notif.humidityLabel': 'ความชื้นอากาศ (%)',
    'admin.notif.soilLabel': 'ความชื้นดิน (%)',
    'admin.notif.min': 'ต่ำสุด',
    'admin.notif.max': 'สูงสุด',
    'admin.notif.testBtn': 'ทดสอบส่งข้อความ',
    'admin.notif.saveBtn': 'บันทึกการตั้งค่า',
    'admin.notif.saveSuccess': 'บันทึกการตั้งค่าสำเร็จ',
    'admin.notif.testSuccess': 'ส่งข้อความทดสอบสำเร็จ',

    'admin.alert.subtitle': 'ดู Alert ทั้งหมด',
    'admin.alert.statTotal': 'ทั้งหมด',
    'admin.alert.statPending': 'รอรับทราบ',
    'admin.alert.statCritical': 'วิกฤต',
    'admin.alert.statAcked': 'รับทราบแล้ว',
    'admin.alert.allProjects': 'ทุกโปรเจกต์',
    'admin.alert.allLevels': 'ทุกระดับ',
    'admin.alert.allStatus': 'ทั้งหมด',
    'admin.alert.pending': 'ยังไม่รับทราบ',
    'admin.alert.acked': 'รับทราบแล้ว',
    'admin.alert.ackedBy': 'โดย',
    'admin.alert.waitAck': 'รอรับทราบ',
    'admin.alert.ackBtn': 'รับทราบ',
    'admin.alert.ackSuccess': 'รับทราบแล้ว',
    'admin.alert.ackAllConfirm': 'รับทราบ Alert ทั้งหมดที่ยังไม่ได้รับทราบ?',
    'admin.alert.ackAllBtn': 'รับทราบทั้งหมด',
    'admin.alert.ackAllSuccess': 'รับทราบทั้งหมดแล้ว',
    'admin.alert.severityCritical': 'วิกฤต',
    'admin.alert.severityWarning': 'เตือน',
    'admin.alert.severityInfo': 'ข้อมูล',
    'admin.alert.colTime': 'เวลา',
    'admin.alert.colLevel': 'ระดับ',
    'admin.alert.colGreenhouse': 'โรงเรือน',
    'admin.alert.colMessage': 'ข้อความ',
    'admin.alert.noAlert': 'ไม่มี Alert',
    'admin.alert.dirAbove': 'สูงกว่า',
    'admin.alert.dirBelow': 'ต่ำกว่า',
    'admin.alert.showCount': 'แสดง {shown} จาก {total} รายการ',

    'admin.audit.subtitle': 'บันทึกการใช้งานระบบทั้งหมด',
    'admin.audit.statLogs': 'บันทึก (7 วัน)',
    'admin.audit.statUsers': 'ผู้ใช้งาน',
    'admin.audit.topActions': 'Action ยอดนิยม',
    'admin.audit.allActions': 'ทุก Action',
    'admin.audit.colTime': 'เวลา',
    'admin.audit.colUser': 'ผู้ใช้',
    'admin.audit.colAction': 'Action',
    'admin.audit.colProjectGh': 'Project/GH',
    'admin.audit.colDetail': 'รายละเอียด',
    'admin.audit.noLog': 'ไม่มีบันทึก',
    'admin.audit.system': 'System',
    'admin.audit.showCount': 'แสดง {shown} จาก {total} รายการ',
    'admin.audit.exportCsv': 'Export CSV',

    'admin.settings.subtitle': 'ดูข้อมูลและตั้งค่าระบบทั่วไป',
    'admin.settings.systemInfo': 'ข้อมูลระบบ',
    'admin.settings.version': 'เวอร์ชัน',
    'admin.settings.nodejs': 'Node.js',
    'admin.settings.database': 'ฐานข้อมูล',
    'admin.settings.dbMain': 'PostgreSQL Database',
    'admin.settings.dbMainDesc': 'ไฟล์ฐานข้อมูลหลัก',
    'admin.settings.connected': 'เชื่อมต่อแล้ว',
    'admin.settings.backup': 'การสำรองข้อมูล',
    'admin.settings.backupEnabled': 'เปิดใช้งาน',
    'admin.settings.backupDisabled': 'ปิดใช้งาน',
    'admin.settings.backupEvery': 'ทุก {n} วัน',
    'admin.settings.backupBtn': 'สำรองข้อมูล',
    'admin.settings.security': 'ความปลอดภัย',
    'admin.settings.sessionTimeout': 'Session Timeout',
    'admin.settings.sessionTimeoutValue': '24 ชั่วโมง',
    'admin.settings.auditLog': 'บันทึกการใช้งาน (Audit Log)',
    'admin.settings.auditLogDesc': 'ระบบจะบันทึกการกระทำทั้งหมดของผู้ใช้ เช่น การ login, การควบคุมอุปกรณ์, การแก้ไขข้อมูล เป็นต้น',
    'admin.settings.auditLogTip': 'สามารถดู Audit Log ได้จากเมนู Audit Log หรือส่งออกเป็น CSV ได้',
    'admin.settings.refresh': 'รีเฟรชข้อมูล',
    'admin.settings.loadError': 'ไม่สามารถโหลดข้อมูลได้',

    'admin.security.subtitle': 'ความปลอดภัยระบบ',
    'admin.security.tabHistory': 'ประวัติ Login',
    'admin.security.tabSessions': 'Sessions',
    'admin.security.tabIp': 'IP Whitelist',
    'admin.security.statTotal': 'Login ทั้งหมด',
    'admin.security.statSuccess': 'สำเร็จ',
    'admin.security.statFailed': 'ล้มเหลว',
    'admin.security.statBlocked': 'ถูกบล็อก',
    'admin.security.statUsers': 'ผู้ใช้',
    'admin.security.colTime': 'เวลา',
    'admin.security.colUser': 'ผู้ใช้',
    'admin.security.colIp': 'IP',
    'admin.security.colStatus': 'สถานะ',
    'admin.security.colLastActive': 'ใช้งานล่าสุด',
    'admin.security.colExpires': 'หมดอายุ',
    'admin.security.colDescription': 'คำอธิบาย',
    'admin.security.colAddedBy': 'เพิ่มโดย',
    'admin.security.colDate': 'วันที่',
    'admin.security.statusSuccess': 'สำเร็จ',
    'admin.security.statusFailed': 'ล้มเหลว',
    'admin.security.statusBlocked': 'ถูกบล็อก',
    'admin.security.terminateConfirm': 'ยกเลิก Session นี้?',
    'admin.security.terminateSuccess': 'ยกเลิก Session สำเร็จ',
    'admin.security.terminateBtn': 'ยกเลิก',
    'admin.security.noSession': 'ไม่มี Session',
    'admin.security.ipWhitelist': 'IP Whitelist',
    'admin.security.ipWhitelistDesc': 'อนุญาตเฉพาะ IP ที่กำหนด',
    'admin.security.ipEnabled': 'เปิด',
    'admin.security.ipDisabled': 'ปิด',
    'admin.security.ipWarning': '⚠️ ระวัง: ถ้าเปิดแล้วไม่ได้เพิ่ม IP ตัวเองจะ Login ไม่ได้!',
    'admin.security.addIp': 'เพิ่ม IP',
    'admin.security.ipPlaceholder': 'IP Address (เช่น 192.168.1.1)',
    'admin.security.ipDescPlaceholder': 'คำอธิบาย',
    'admin.security.addIpSuccess': 'เพิ่ม IP สำเร็จ',
    'admin.security.removeIpConfirm': 'ลบ IP นี้?',
    'admin.security.removeIpSuccess': 'ลบ IP สำเร็จ',
    'admin.security.noIp': 'ยังไม่มี IP',
    'admin.security.toggleOnSuccess': 'เปิด IP Whitelist',
    'admin.security.toggleOffSuccess': 'ปิด IP Whitelist',
    'admin.security.error': 'เกิดข้อผิดพลาด',

    'admin.auto.subtitle': 'กฎอัตโนมัติ - ถ้า X แล้ว Y',
    'admin.auto.selectProject': 'โปรเจกต์',
    'admin.auto.selectProjectPlaceholder': 'เลือกโปรเจกต์...',
    'admin.auto.selectGh': 'โรงเรือน',
    'admin.auto.selectGhPlaceholder': 'เลือกโรงเรือน...',
    'admin.auto.addRule': 'เพิ่มกฎ',
    'admin.auto.pleaseSelect': 'กรุณาเลือกโปรเจกต์และโรงเรือน',
    'admin.auto.noRule': 'ยังไม่มีกฎอัตโนมัติ',
    'admin.auto.lastTriggered': 'ทำงานล่าสุด:',
    'admin.auto.triggerCount': '{n} ครั้ง',
    'admin.auto.toggleSuccess': 'เปิดกฎแล้ว',
    'admin.auto.toggleOffSuccess': 'ปิดกฎแล้ว',
    'admin.auto.deleteConfirm': 'ลบกฎ "{name}"?',
    'admin.auto.deleteSuccess': 'ลบสำเร็จ',
    'admin.auto.error': 'เกิดข้อผิดพลาด',
    'admin.auto.loadError': 'ไม่สามารถโหลดข้อมูลได้',
    'admin.auto.triggerSensor': 'เมื่อค่า Sensor',
    'admin.auto.triggerTime': 'ตามเวลา',
    'admin.auto.triggerManual': 'กดเอง',
    'admin.auto.condAbove': 'มากกว่า',
    'admin.auto.condBelow': 'น้อยกว่า',
    'admin.auto.condEqual': 'เท่ากับ',
    'admin.auto.condBetween': 'อยู่ระหว่าง',
    'admin.auto.timeAt': 'เวลา',
    'admin.auto.actionsCount': '{n} Actions',
    'admin.auto.createTitle': 'สร้างกฎใหม่',
    'admin.auto.editTitle': 'แก้ไขกฎ',
    'admin.auto.fieldName': 'ชื่อกฎ',
    'admin.auto.fieldNamePlaceholder': 'เช่น เปิดพัดลมเมื่อร้อน',
    'admin.auto.fieldDesc': 'คำอธิบาย (optional)',
    'admin.auto.fieldDescPlaceholder': 'รายละเอียดเพิ่มเติม',
    'admin.auto.triggerType': 'ประเภท Trigger',
    'admin.auto.sensorKey': 'Sensor Key',
    'admin.auto.sensorKeyPlaceholder': 'เช่น air_temp',
    'admin.auto.condition': 'เงื่อนไข',
    'admin.auto.value': 'ค่า',
    'admin.auto.time': 'เวลา',
    'admin.auto.actions': 'Actions (สิ่งที่จะทำ)',
    'admin.auto.addAction': 'เพิ่ม Action',
    'admin.auto.actionControl': 'ควบคุมอุปกรณ์',
    'admin.auto.actionNotif': 'ส่งแจ้งเตือน',
    'admin.auto.controlKeyPlaceholder': 'Control Key (เช่น fan_1)',
    'admin.auto.actionOn': 'เปิด',
    'admin.auto.actionOff': 'ปิด',
    'admin.auto.actionToggle': 'สลับ',
    'admin.auto.messagePlaceholder': 'ข้อความ',
    'admin.auto.saveSuccess': 'บันทึกสำเร็จ',

    'admin.scene.subtitle': 'ชุดคำสั่งสำเร็จรูป เช่น เช้า กลางวัน เย็น',
    'admin.scene.selectProject': 'โปรเจกต์',
    'admin.scene.selectProjectPlaceholder': 'เลือกโปรเจกต์...',
    'admin.scene.selectGh': 'โรงเรือน',
    'admin.scene.selectGhPlaceholder': 'เลือกโรงเรือน...',
    'admin.scene.addScene': 'สร้าง Scene',
    'admin.scene.pleaseSelect': 'กรุณาเลือกโปรเจกต์และโรงเรือน',
    'admin.scene.noScene': 'ยังไม่มี Scene',
    'admin.scene.executeSuccess': 'ทำ "{name}" สำเร็จ',
    'admin.scene.deleteConfirm': 'ลบ Scene "{name}"?',
    'admin.scene.deleteSuccess': 'ลบสำเร็จ',
    'admin.scene.error': 'เกิดข้อผิดพลาด',
    'admin.scene.loadError': 'ไม่สามารถโหลดข้อมูลได้',
    'admin.scene.executeBtn': 'ทำ',
    'admin.scene.iconLabel': 'Icon',
    'admin.scene.iconLightning': 'ฟ้าผ่า',
    'admin.scene.iconDay': 'กลางวัน',
    'admin.scene.iconNight': 'กลางคืน',
    'admin.scene.iconMorning': 'เช้า',
    'admin.scene.colorLabel': 'สี',
    'admin.scene.createTitle': 'สร้าง Scene ใหม่',
    'admin.scene.editTitle': 'แก้ไข Scene',
    'admin.scene.fieldName': 'ชื่อ Scene',
    'admin.scene.fieldNamePlaceholder': 'เช่น เช้า, กลางวัน, เย็น',
    'admin.scene.fieldDesc': 'คำอธิบาย',
    'admin.scene.actions': 'Actions',
    'admin.scene.actionOn': 'เปิด',
    'admin.scene.actionOff': 'ปิด',
    'admin.scene.saveSuccess': 'บันทึกสำเร็จ',

    'admin.ctrl.subtitle': 'บันทึกการเปิด/ปิดอุปกรณ์ทั้งหมด',
    'admin.ctrl.selectProject': 'โปรเจกต์',
    'admin.ctrl.selectProjectPlaceholder': 'เลือกโปรเจกต์...',
    'admin.ctrl.selectGh': 'โรงเรือน',
    'admin.ctrl.selectGhPlaceholder': 'เลือกโรงเรือน...',
    'admin.ctrl.loadBtn': 'โหลดประวัติ',
    'admin.ctrl.pleaseSelect': 'กรุณาเลือกโปรเจกต์และโรงเรือน',
    'admin.ctrl.statTotal': 'ทั้งหมด',
    'admin.ctrl.statManual': 'กดเอง',
    'admin.ctrl.statSchedule': 'ตั้งเวลา',
    'admin.ctrl.statAuto': 'อัตโนมัติ',
    'admin.ctrl.srcManual': 'กดเอง',
    'admin.ctrl.srcSchedule': 'ตั้งเวลา',
    'admin.ctrl.srcAuto': 'อัตโนมัติ',
    'admin.ctrl.srcScene': 'Scene',
    'admin.ctrl.colTime': 'เวลา',
    'admin.ctrl.colDevice': 'อุปกรณ์',
    'admin.ctrl.colAction': 'การกระทำ',
    'admin.ctrl.colSource': 'แหล่งที่มา',
    'admin.ctrl.colUser': 'ผู้ใช้',
    'admin.ctrl.actionOn': 'เปิด',
    'admin.ctrl.actionOff': 'ปิด',
    'admin.ctrl.noHistory': 'ยังไม่มีประวัติ',
    'admin.ctrl.loadError': 'ไม่สามารถโหลดข้อมูลได้',

    'role.superadmin': 'Super Admin',
    'role.admin': 'Admin',
    'role.operator': 'Operator',
    'role.viewer': 'Viewer',

    'msg.success': 'สำเร็จ',
    'msg.error': 'เกิดข้อผิดพลาด',
    'msg.saved': 'บันทึกสำเร็จ',
    'msg.deleted': 'ลบสำเร็จ',
    'msg.confirmDelete': 'ยืนยันการลบ?',
    'msg.noPermission': 'คุณไม่มีสิทธิ์ดำเนินการนี้',

    'profile.title': 'โปรไฟล์',
    'profile.subtitle': 'จัดการข้อมูลส่วนตัวและการตั้งค่า',
    'profile.lastLogin': 'เข้าสู่ระบบล่าสุด:',
    'profile.createdAt': 'สร้างเมื่อ:',
    'profile.changePassword': 'เปลี่ยนรหัสผ่าน',
    'profile.currentPassword': 'รหัสผ่านปัจจุบัน',
    'profile.newPassword': 'รหัสผ่านใหม่',
    'profile.confirmPassword': 'ยืนยันรหัสผ่านใหม่',
    'profile.passwordMismatch': 'รหัสผ่านใหม่ไม่ตรงกัน',
    'profile.passwordMin6': 'รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร',
    'profile.passwordChanged': 'เปลี่ยนรหัสผ่านสำเร็จ',
    'profile.preferencesTitle': 'การตั้งค่าส่วนตัว',
    'profile.language': 'ภาษา',
    'profile.theme': 'ธีม',
    'profile.themeLight': '☀️ สว่าง',
    'profile.themeDark': '🌙 มืด',
    'profile.themeSystem': '💻 ตามระบบ',
    'profile.savePreferences': 'บันทึกการตั้งค่า',
    'profile.preferencesSaved': 'บันทึกการตั้งค่าสำเร็จ',
    'profile.strengthWeak': 'อ่อน',
    'profile.strengthMedium': 'ปานกลาง',
    'profile.strengthStrong': 'แข็งแรง',

    'agri.title': 'การเกษตร',
    'agri.subtitle': 'เลือกหมวดหมู่ที่ต้องการจัดการ',
    'agri.menu.crops': 'จัดการพืช',
    'agri.menu.cropsDesc': 'บันทึกและติดตามพืชที่ปลูก',
    'agri.menu.growth': 'บันทึกการเติบโต',
    'agri.menu.growthDesc': 'ติดตามการเจริญเติบโตของพืช',
    'agri.menu.fertilizer': 'ตารางใส่ปุ๋ย',
    'agri.menu.fertilizerDesc': 'วางแผนและติดตามการใส่ปุ๋ย',
    'agri.menu.pestDisease': 'โรค/แมลง',
    'agri.menu.pestDiseaseDesc': 'บันทึกและติดตามปัญหาโรคและแมลง',
    'agri.menu.yield': 'ผลผลิต',
    'agri.menu.yieldDesc': 'ติดตามการเก็บเกี่ยวและรายได้',
    'agri.menu.water': 'การใช้น้ำ',
    'agri.menu.waterDesc': 'ติดตามปริมาณและค่าใช้จ่ายน้ำ',

    'agri.crop.status.planted': 'เพิ่งปลูก',
    'agri.crop.status.growing': 'กำลังเติบโต',
    'agri.crop.status.harvested': 'เก็บเกี่ยวแล้ว',
    'agri.crop.status.failed': 'ล้มเหลว',
    'agri.crop.statTotal': 'พืชทั้งหมด',
    'agri.crop.statNearHarvest': 'ใกล้เก็บ (7 วัน)',
    'agri.crop.addCrop': 'เพิ่มพืช',
    'agri.crop.addTitle': 'เพิ่มพืชใหม่',
    'agri.crop.editTitle': 'แก้ไขพืช',
    'agri.crop.confirmDelete': 'ลบ',
    'agri.crop.confirmHarvest': 'บันทึกการเก็บเกี่ยว',
    'agri.crop.noData': 'ยังไม่มีข้อมูลพืช',
    'agri.crop.colName': 'พืช',
    'agri.crop.colPlantDate': 'วันปลูก',
    'agri.crop.colExpectedHarvest': 'คาดว่าเก็บเกี่ยว',
    'agri.crop.variety': 'พันธุ์',
    'agri.crop.daysLeft': 'อีก {n} วัน',
    'agri.crop.fieldName': 'ชื่อพืช',
    'agri.crop.fieldNamePlaceholder': 'เช่น ผักกาดหอม',
    'agri.crop.fieldVariety': 'พันธุ์',
    'agri.crop.fieldVarietyPlaceholder': 'เช่น เรดโอ๊ค',
    'agri.col.greenhouse': 'โรงเรือน',
    'agri.col.quantity': 'จำนวน',
    'agri.col.unit': 'หน่วย',
    'agri.filter.allProjects': 'ทุกโปรเจกต์',
    'agri.filter.allGreenhouses': 'ทุกโรงเรือน',
    'agri.filter.allStatus': 'ทุกสถานะ',
    'agri.label.project': 'โปรเจกต์',
    'agri.label.greenhouse': 'โรงเรือน',
    'agri.label.select': 'เลือก...',
    'agri.label.notes': 'บันทึก',
    'agri.label.notesPlaceholder': 'รายละเอียดเพิ่มเติม...',
    'agri.unit.plant': 'ต้น',
    'agri.unit.tray': 'ถาด',
    'agri.unit.plot': 'แปลง',
    'agri.unit.kg': 'กก.',

    'agri.fertilizer.overdue': 'เกินกำหนด',
    'agri.fertilizer.today': 'วันนี้',
    'agri.fertilizer.upcoming': 'กำลังจะมา',
    'agri.fertilizer.completed': 'เสร็จแล้ว',
    'agri.fertilizer.showCompleted': 'แสดงที่เสร็จแล้ว',
    'agri.fertilizer.addSchedule': 'เพิ่มตาราง',
    'agri.fertilizer.addTitle': 'เพิ่มตารางใส่ปุ๋ย',
    'agri.fertilizer.editTitle': 'แก้ไขตารางใส่ปุ๋ย',
    'agri.fertilizer.addSuccess': 'เพิ่มตารางสำเร็จ',
    'agri.fertilizer.completeSuccess': 'บันทึกสำเร็จ',
    'agri.fertilizer.completedAt': 'เสร็จ',
    'agri.fertilizer.confirmDelete': 'ลบ',
    'agri.fertilizer.noData': 'ยังไม่มีตารางใส่ปุ๋ย',
    'agri.fertilizer.fieldName': 'ชื่อปุ๋ย',
    'agri.fertilizer.fieldNamePlaceholder': 'เช่น ปุ๋ยเคมี 16-16-16',
    'agri.fertilizer.fieldType': 'ประเภท',
    'agri.fertilizer.fieldDate': 'วันที่',
    'agri.fertilizer.fieldAmount': 'ปริมาณ',
    'agri.fertilizer.typeChemical': 'ปุ๋ยเคมี',
    'agri.fertilizer.typeOrganic': 'ปุ๋ยอินทรีย์',
    'agri.fertilizer.typeBio': 'ปุ๋ยชีวภาพ',
    'agri.fertilizer.typeFoliar': 'ปุ๋ยทางใบ',

    'agri.growth.health.excellent': 'ดีมาก',
    'agri.growth.health.good': 'ดี',
    'agri.growth.health.fair': 'ปานกลาง',
    'agri.growth.health.poor': 'แย่',
    'agri.growth.selectCrop': 'เลือกพืช',
    'agri.growth.selectCropPlaceholder': 'เลือกพืชที่ต้องการติดตาม...',
    'agri.growth.addRecord': 'เพิ่มบันทึก',
    'agri.growth.addTitle': 'เพิ่มบันทึกการเติบโต',
    'agri.growth.editTitle': 'แก้ไขบันทึก',
    'agri.growth.pleaseSelect': 'กรุณาเลือกพืช',
    'agri.growth.confirmDelete': 'ลบบันทึกนี้?',
    'agri.growth.noData': 'ยังไม่มีบันทึก',
    'agri.growth.totalGrowth': 'เติบโตทั้งหมด',
    'agri.growth.avgRate': 'อัตราเติบโตเฉลี่ย',
    'agri.growth.recordCount': 'จำนวนบันทึก',
    'agri.growth.day': 'วัน',
    'agri.growth.colDate': 'วันที่',
    'agri.growth.colHeight': 'ความสูง',
    'agri.growth.colLeafCount': 'จำนวนใบ',
    'agri.growth.colHealth': 'สุขภาพ',
    'agri.growth.colPhoto': 'รูป',
    'agri.growth.colRecordedBy': 'ผู้บันทึก',
    'agri.growth.viewPhoto': 'ดูรูป',
    'agri.growth.fieldHeight': 'ความสูง (cm)',
    'agri.growth.fieldLeafCount': 'จำนวนใบ',
    'agri.growth.fieldPhotoUrl': 'URL รูปภาพ',

    'agri.pest.title': 'โรคและแมลงศัตรูพืช',
    'agri.pest.subtitle': 'บันทึกและติดตามปัญหาโรคและแมลง',
    'agri.pest.typePest': 'แมลงศัตรูพืช',
    'agri.pest.typeDisease': 'โรคพืช',
    'agri.pest.typePestShort': 'แมลง',
    'agri.pest.typeDiseaseShort': 'โรค',
    'agri.pest.severity.low': 'น้อย',
    'agri.pest.severity.medium': 'ปานกลาง',
    'agri.pest.severity.high': 'มาก',
    'agri.pest.severity.critical': 'รุนแรง',
    'agri.pest.statCritical': 'ความรุนแรงสูง',
    'agri.pest.statResolved': 'แก้ไขแล้ว',
    'agri.pest.filterAll': 'ทุกประเภท',
    'agri.pest.showResolved': 'แสดงที่แก้ไขแล้ว',
    'agri.pest.addRecord': 'บันทึกปัญหา',
    'agri.pest.addTitle': 'บันทึกโรค/แมลงศัตรูพืช',
    'agri.pest.editTitle': 'แก้ไขบันทึก',
    'agri.pest.confirmDelete': 'ลบบันทึกนี้?',
    'agri.pest.resolved': 'แก้ไขแล้ว',
    'agri.pest.markResolved': 'แก้ไขแล้ว',
    'agri.pest.resolveSuccess': 'บันทึกการแก้ไขสำเร็จ',
    'agri.pest.fieldType': 'ประเภท',
    'agri.pest.fieldName': 'ชื่อโรค/แมลง',
    'agri.pest.fieldNamePlaceholder': 'เช่น เพลี้ยอ่อน, โรคราน้ำค้าง',
    'agri.pest.fieldSeverity': 'ความรุนแรง',
    'agri.pest.fieldAffectedArea': 'พื้นที่ที่พบ',
    'agri.pest.fieldAffectedAreaPlaceholder': 'เช่น แปลงที่ 1-3',
    'agri.pest.fieldTreatment': 'การรักษา/กำจัด',
    'agri.pest.fieldTreatmentPlaceholder': 'เช่น พ่นยาฆ่าแมลงชีวภาพ',

    'agri.water.statTotalM3': 'ใช้รวม (m³)',
    'agri.water.statAvgPerDay': 'เฉลี่ย/วัน (L)',
    'agri.water.statTotalCost': 'ค่าใช้จ่ายรวม',
    'agri.water.statPerM3': 'ต่อ m³',
    'agri.water.chartTitle': 'ปริมาณการใช้น้ำ',
    'agri.water.addRecord': 'บันทึกการใช้น้ำ',
    'agri.water.addTitle': 'บันทึกการใช้น้ำ',
    'agri.water.editTitle': 'แก้ไขบันทึก',
    'agri.water.confirmDelete': 'ลบบันทึกนี้?',
    'agri.water.noData': 'ยังไม่มีข้อมูล',
    'agri.water.colUsage': 'ปริมาณ (L)',
    'agri.water.colSource': 'แหล่งน้ำ',
    'agri.water.colCost': 'ค่าใช้จ่าย',
    'agri.water.fieldUsage': 'ปริมาณ (ลิตร)',
    'agri.water.fieldCost': 'ค่าใช้จ่าย (฿)',
    'agri.water.source.tap': 'น้ำประปา',
    'agri.water.source.well': 'น้ำบาดาล',
    'agri.water.source.rain': 'น้ำฝน',
    'agri.water.source.river': 'แหล่งน้ำธรรมชาติ',

    'agri.yield.gradeA': 'เกรด A',
    'agri.yield.gradeB': 'เกรด B',
    'agri.yield.gradeC': 'เกรด C',
    'agri.yield.gradeReject': 'คัดทิ้ง',
    'agri.yield.statHarvestCount': 'ครั้งที่เก็บเกี่ยว',
    'agri.yield.statTotalYield': 'ผลผลิตรวม (kg)',
    'agri.yield.statTotalRevenue': 'รายได้รวม',
    'agri.yield.statAvgPrice': 'ราคาเฉลี่ย/kg',
    'agri.yield.chartTitle': 'ผลผลิตรายเดือน',
    'agri.yield.chartQuantity': 'ปริมาณ (kg)',
    'agri.yield.chartRevenue': 'รายได้ (฿)',
    'agri.yield.addRecord': 'บันทึกผลผลิต',
    'agri.yield.addTitle': 'บันทึกผลผลิต',
    'agri.yield.editTitle': 'แก้ไขบันทึก',
    'agri.yield.confirmDelete': 'ลบบันทึกนี้?',
    'agri.yield.noData': 'ยังไม่มีข้อมูล',
    'agri.yield.colCrop': 'พืช',
    'agri.yield.colQuantity': 'ปริมาณ',
    'agri.yield.colGrade': 'เกรด',
    'agri.yield.colPricePerUnit': 'ราคา/หน่วย',
    'agri.yield.colRevenue': 'รายได้',
    'agri.yield.fieldHarvestDate': 'วันที่เก็บเกี่ยว',
    'agri.yield.fieldPricePerUnit': 'ราคาต่อหน่วย (฿)',
    'agri.yield.totalRevenue': 'รายได้รวม',
    'agri.yield.unitKg': 'กิโลกรัม (kg)',
    'agri.yield.unitG': 'กรัม (g)',
    'agri.yield.unitPcs': 'ชิ้น',
    'agri.yield.unitBunch': 'กำ',
    
    'soil.airSection': 'ค่าอากาศ',
    'soil.soilSection': 'ค่าดิน (10 จุด)',
    'soil.systemSection': 'ข้อมูลระบบ',

    'charts.range1h': '1 ชั่วโมง',
    'charts.range6h': '6 ชั่วโมง',
    'charts.range24h': '24 ชั่วโมง',
    'charts.range7d': '7 วัน',
    'charts.range30d': '30 วัน',
    'charts.rangeCustom': 'กำหนดเอง',
    'charts.soilMoisture': 'ความชื้น',
    'charts.soilTemp': 'อุณหภูมิ',
    'charts.errSelectDate': 'กรุณาเลือกวันที่เริ่มต้นและสิ้นสุด',
    'charts.errStartBeforeEnd': 'วันที่เริ่มต้นต้องน้อยกว่าวันที่สิ้นสุด',
    'charts.errMax90Days': 'สามารถเลือกข้อมูลได้สูงสุด 90 วัน',
    'charts.errSelectRange': 'กรุณาเลือกช่วงเวลา',
    'charts.loadError': 'ไม่สามารถโหลดข้อมูลกราฟได้',
    'charts.notReady': 'โรงเรือนนี้ยังไม่พร้อมแสดงกราฟ',
    'charts.airChartTitle': 'กราฟค่าอากาศ',
    'charts.soilChartTitle': 'กราฟค่าดิน',
    'charts.fullscreen': 'ขยายเต็มจอ',
    'charts.allNodes': 'ทั้ง 10 จุด',
    'charts.selectNode': 'เลือกจุด',
    'charts.show': 'แสดง',
    'charts.params': 'พารามิเตอร์',
    'charts.nodeLabel': 'จุดที่',
    'charts.node': 'จุดที่',
    'charts.timeRange': 'ช่วงเวลา',
    'charts.customRangeTitle': 'กำหนดช่วงเวลาเอง (สูงสุด 90 วัน)',
    'charts.startDate': 'วันที่เริ่มต้น',
    'charts.endDate': 'วันที่สิ้นสุด',
    'charts.showingData': 'แสดงข้อมูล',
    'charts.to': 'ถึง',

    'timers.fan1': 'พัดลมใหญ่',
    'timers.fan2': 'พัดลมกวนอากาศ',
    'timers.valve1': 'เปิดน้ำโซน 1',
    'timers.valve2': 'เปิดน้ำโซน 2',
    'timers.valve3': 'เปิดน้ำโซน 3',
    'timers.valve4': 'เปิดน้ำโซน 4',
    'timers.light1': 'แสงเสริม',
    'timers.motor': 'มอเตอร์ (รวม)',
    'timers.saveSuccess': 'บันทึกเวลาสำเร็จ',
    'timers.invalidFormat': 'รูปแบบเวลาไม่ถูกต้อง (HH:mm)',
    'timers.noPermission': 'คุณไม่มีสิทธิ์ตั้งเวลา (ต้องเป็น superadmin / operator / admin)',
    'timers.infoNote': '⏰ ตั้งเวลาเปิด-ปิดอุปกรณ์อัตโนมัติ (ต้องเปิดโหมด Auto ในแท็บควบคุมด้วย)',
    'timers.onTime': 'เวลาเปิด',
    'timers.offTime': 'เวลาปิด',
    'timers.motorDesc': 'ตั้งเวลาเคลื่อนที่ (Forward/Reverse)',
    'timers.forwardTime': 'เวลา Forward (ลง)',
    'timers.reverseTime': 'เวลา Reverse (ขึ้น)',

    'auto.water': 'เปิดน้ำ (4 โซน)',
    'auto.motorShade': 'ระบบพรางแสง (4 โซน)',
    'auto.sensorAirTemp': 'อุณหภูมิอากาศ',
    'auto.sensorAirHumidity': 'ความชื้นอากาศ',
    'auto.sensorLight': 'แสง',
    'auto.sensorCo2': 'CO2',
    'auto.sensorSoilMoisture': 'ความชื้นดิน จุดที่ {n}',
    'auto.condGt': 'มากกว่า (>)',
    'auto.condLt': 'น้อยกว่า (<)',
    'auto.condGte': 'มากกว่าหรือเท่ากับ (≥)',
    'auto.condLte': 'น้อยกว่าหรือเท่ากับ (≤)',
    'auto.modeDaily': 'ตั้งเวลา (Daily)',
    'auto.modeCondition': 'การทำงานตามเงื่อนไข',
    'auto.modeCycle': 'การทํางานเป็นรอบ',
    'auto.title': 'ระบบทำงาน Auto',
    'auto.subtitle': 'ควบคุมอัตโนมัติด้วย 3 ระบบอัจฉริยะ',
    'auto.overview': 'ภาพรวม',
    'auto.configMode': 'ตั้งค่าโหมด Auto',
    'auto.cardDaily': 'ตั้งเวลาเปิด/ปิด',
    'auto.cardCondition': 'การทำงานตามเงื่อนไข',
    'auto.cardCycle': 'การทํางานเป็นรอบ',
    'auto.realtimeStatus': 'สถานะการทำงานแบบ Real-time',
    'auto.updating': 'กำลังอัปเดต...',
    'auto.working': 'Working',
    'auto.idle': 'Idle',
    'auto.controlSystem': 'ระบบควบคุม',
    'auto.deviceOffline': 'อุปกรณ์ออฟไลน์',
    'auto.offlineDesc': 'ระบบอัตโนมัติจะทำงานตามค่าล่าสุดที่บันทึกไว้ในตัวเครื่อง',

    'air.staleData': 'ข้อมูลไม่อัพเดท',
    'air.outOfRange': 'ค่าผิดปกติ',
    'air.temperature': 'อุณหภูมิ',
    'air.tempWarning': 'อุณหภูมิผิดปกติ',
    'air.humidity': 'ความชื้น',
    'air.humidityWarning': 'ความชื้นผิดปกติ',
    'air.co2Warning': 'CO₂ สูงเกินไป',
    'air.light': 'แสง',

    'autoDevice.sequential': 'ระบบหมุนเวียน 4 โซน',
    'autoDevice.allZones': 'ทำงานพร้อมกัน 4 โซน',
    'autoDevice.autoControl': 'ควบคุมอัตโนมัติ',
    'autoDevice.currentMode': 'โหมดปัจจุบัน',
    'autoDevice.unknown': 'ไม่ทราบ',
    'autoDevice.tabSchedule': 'ตั้งเวลา',
    'autoDevice.tabCondition': 'ตามเงื่อนไข',
    'autoDevice.tabCycle': 'รอบเวลา',
    'autoDevice.dailyInfo': 'ตั้งเวลาเปิด-ปิดแบบง่าย วนซ้ำทุกวัน',
    'autoDevice.dailyGoTo': 'ไปตั้งค่าที่แท็บ "ตั้งเวลา" ของอุปกรณ์แต่ละตัว',
    'autoDevice.enable': 'เปิดใช้งาน',
    'autoDevice.conditionDesc': 'ควบคุมตามเงื่อนไขเซ็นเซอร์',
    'autoDevice.selectSensor': 'เลือกเซ็นเซอร์',
    'autoDevice.condition': 'เงื่อนไข',
    'autoDevice.targetValue': 'ค่าที่ต้องการ',
    'autoDevice.whenTrue': 'เมื่อเงื่อนไขเป็นจริง',
    'autoDevice.actionForward': 'เดินหน้า',
    'autoDevice.actionReverse': 'ย้อนกลับ',
    'autoDevice.actionOn': 'เปิด',
    'autoDevice.actionOff': 'ปิด',
    'autoDevice.exampleTitle': 'ตัวอย่างการทำงาน',
    'autoDevice.saving': 'กำลังบันทึก...',
    'autoDevice.saveBtn': 'บันทึกการตั้งค่า',
    'autoDevice.cycleDesc': 'ทำงานเป็นรอบๆ',
    'autoDevice.timeRange': 'ช่วงเวลาทำงาน',
    'autoDevice.start': 'เริ่ม',
    'autoDevice.end': 'สิ้นสุด',
    'autoDevice.onTimePerZone': 'เวลาต่อโซน (นาที)',
    'autoDevice.onTime': 'เวลาเปิด (นาที)',
    'autoDevice.waitBetween': 'รอระหว่างรอบ (นาที)',
    'autoDevice.offTime': 'เวลาปิด (นาที)',
    'autoDevice.maxCycles': 'จำนวนรอบสูงสุด',
    'autoDevice.maxCyclesHint': 'ระบุได้ 1-10 รอบ',
    'autoDevice.exampleCycle1': 'ตัวอย่างการทำงาน (รอบที่ 1):',
    'autoDevice.zone1': 'เปิดน้ำโซน 1',
    'autoDevice.zone2': 'เปิดน้ำโซน 2',
    'autoDevice.zone3': 'เปิดน้ำโซน 3',
    'autoDevice.zone4': 'เปิดน้ำโซน 4',
    'autoDevice.cycleWait': 'รอ {off} นาที แล้ววนรอบใหม่ (สูงสุด {max} รอบ)',
    'autoDevice.cycleWarning': '⚠️ หากใกล้เวลาสิ้นสุด ระบบจะทำให้ครบ 4 โซนก่อนหยุด',
    'autoDevice.cycleExample': 'ช่วงเวลา {start}-{end} → เปิด {on} นาที, ปิด {off} นาที, วนซ้ำ {max} รอบ',

    'autoMode.title': 'โหมดทำงานตามเวลาอัตโนมัติ',
    'autoMode.subtitle': 'เปิด/ปิดการควบคุมการทำงานตามเวลาแบบอัตโนมัติ',
    'autoMode.infoText': 'เมื่อเปิดโหมดอัตโนมัติ อุปกรณ์จะทำงานตามตัวจับเวลาที่ตั้งไว้',
    'autoMode.on': 'เปิด',
    'autoMode.off': 'ปิด',
    'autoMode.fan1': 'พัดลมใหญ่',
    'autoMode.fan2': 'พัดลมกวนอากาศ',
    'autoMode.valve1': 'น้ำโซน 1',
    'autoMode.valve2': 'น้ำโซน 2',
    'autoMode.valve3': 'น้ำโซน 3',
    'autoMode.valve4': 'น้ำโซน 4',
    'autoMode.light1': 'แสงเสริม',
    'autoMode.motor': 'มอเตอร์ทั้งหมด',

    'banner.developingTitle': 'กำลังพัฒนา',
    'banner.developingDefault': 'ยังไม่มีการผูก ThingsBoard Device (deviceId)',
    'banner.developingNote': 'ค่าทั้งหมดจะแสดงเป็น -- และการควบคุมทั้งหมดจะถูกล็อก',
    'banner.offlineTitle': 'อุปกรณ์ออฟไลน์',
    'banner.offlineDesc': 'ไม่สามารถเชื่อมต่อกับอุปกรณ์ได้ การควบคุมทั้งหมดจะถูกล็อก',

    'export.title': 'Export ข้อมูล',
    'export.format': 'รูปแบบไฟล์',
    'export.dateRange': 'ช่วงเวลา',
    'export.last7': '7 วันล่าสุด',
    'export.last30': '30 วันล่าสุด',
    'export.last90': '90 วันล่าสุด',
    'export.startDate': 'วันที่เริ่มต้น',
    'export.endDate': 'วันที่สิ้นสุด',
    'export.maxNote': '* สามารถเลือกได้สูงสุด 1 ปี',
    'export.airSensors': 'เซนเซอร์อากาศ',
    'export.soilNodes': 'จุดดิน',
    'export.soilNode': 'จุดที่ {n}',
    'export.soilParams': 'พารามิเตอร์ดิน',
    'export.downloadBtn': 'ดาวน์โหลด',
    'export.downloading': 'กำลังดาวน์โหลด...',
    'export.btnLabel': 'Export',
    'export.successMsg': 'ดาวน์โหลดข้อมูลสำเร็จ ({n} วัน)',
    'export.errNoDate': 'กรุณาเลือกวันที่เริ่มต้นและสิ้นสุด',
    'export.errStartEnd': 'วันที่เริ่มต้นต้องน้อยกว่าวันที่สิ้นสุด',
    'export.errMax1Year': 'สามารถ Export ได้สูงสุด 1 ปี',
    'export.errNoSensor': 'กรุณาเลือกเซนเซอร์อย่างน้อย 1 ตัว',
    'export.errFailed': 'ไม่สามารถ Export ได้',

    'exportSimple.selectRange': 'เลือกช่วงเวลา',
    'exportSimple.days': '{n} วัน',
    'exportSimple.successMsg': 'ดาวน์โหลดข้อมูล {n} วันสำเร็จ',

    'motor.notReady': 'ไม่พร้อม',
    'motor.shading': '🌥️ กำลังพรางแสง',
    'motor.notShading': '☀️ ไม่พรางแสง',
    'motor.stopped': '⏸️ หยุด',
    'motor.shade': 'พรางแสง',
    'motor.unshade': 'ไม่พราง',

    'relay.working': 'กำลังทำงาน',
    'relay.idle': 'หยุด',

    'report.title': 'ดาวน์โหลดรายงาน PDF',
    'report.subtitle': 'สรุปข้อมูล sensor และสถิติ',
    'report.period1d': 'รายวัน',
    'report.period1dDesc': '24 ชั่วโมงย้อนหลัง',
    'report.period7d': 'รายสัปดาห์',
    'report.period7dDesc': '7 วันย้อนหลัง',
    'report.period30d': 'รายเดือน',
    'report.period30dDesc': '30 วันย้อนหลัง',
    'report.shareTitle': 'บันทึกหรือแชร์รายงาน',
    'report.successMsg': 'ดาวน์โหลดรายงานสำเร็จ',
    'report.errFailed': 'ไม่สามารถดาวน์โหลดรายงานได้',
    'report.downloadBtn': 'ดาวน์โหลดรายงาน',

    'soil.nutrients': 'ธาตุอาหาร (mg/kg)',

    'sysinfo.signal': 'สัญญาณ',
    'sysinfo.readTime': 'เวลาอ่านข้อมูล',
    'sysinfo.sigUnknown': 'ไม่ทราบ',
    'sysinfo.sigExcellent': 'ดีมาก',
    'sysinfo.sigGood': 'ดี',
    'sysinfo.sigFair': 'ปานกลาง',
    'sysinfo.sigWeak': 'อ่อน',
    'sysinfo.sigVeryWeak': 'อ่อนมาก',

    'weather.unavailable': 'ไม่สามารถโหลดข้อมูลอากาศได้',
    'weather.updated': 'อัพเดทเมื่อ',
    'weather.data_at': 'ข้อมูล ณ',
    'weather.cached': 'แคช',
    'weather.sectionTitle': 'สภาพอากาศภายนอก',
    'admin.weather': 'ตั้งค่าสภาพอากาศ',
    'admin.weather.subtitle': 'ตั้งค่าตำแหน่งและข้อมูลที่แสดงสำหรับแต่ละโรงเรือน',
    'admin.weather.searchPlaceholder': 'ค้นหาสถานที่...',
    'admin.weather.searchBtn': 'ค้นหา',
    'admin.weather.selectLocation': 'เลือกสถานที่',
    'admin.weather.currentLocation': 'สถานที่ปัจจุบัน',
    'admin.weather.latitude': 'ละติจูด',
    'admin.weather.longitude': 'ลองจิจูด',
    'admin.weather.showFields': 'ข้อมูลที่แสดง',
    'admin.weather.showTemperature': 'อุณหภูมิ',
    'admin.weather.showHumidity': 'ความชื้น',
    'admin.weather.showCondition': 'สภาพอากาศ',
    'admin.weather.showWindSpeed': 'ความเร็วลม',
    'admin.weather.showUvIndex': 'ดัชนี UV',
    'admin.weather.showRainChance': 'โอกาสฝนตก',
    'admin.weather.saveSuccess': 'บันทึกการตั้งค่าสำเร็จ',
    'admin.weather.noConfig': 'ยังไม่มีการตั้งค่า (ใช้ค่าเริ่มต้น กรุงเทพฯ)',
    'admin.calibrate': 'Calibrate Sensor',

  },

  en: {
    'app.title': 'GreenHouse Pro',
    'app.subtitle': 'Smart Greenhouse Management System',


    'project.locked': 'Locked',
    'project.viewGreenhouses': 'View Greenhouses',
    'project.noAccess': 'You do not have access to this project',

    'nav.home': 'Home',
    'nav.agriculture': 'Agriculture',
    'nav.dashboard': 'Dashboard',
    'nav.projects': 'Projects',
    'nav.greenhouses': 'Greenhouses',
    'nav.admin': 'Admin',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',
    'nav.logout': 'Logout',
    'nav.notifications': 'Notifications',

    'auth.login': 'Login',
    'auth.logout': 'Logout',
    'auth.username': 'Username',
    'auth.password': 'Password',
    'auth.remember': 'Remember me',
    'auth.forgot': 'Forgot password?',

    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.refresh': 'Refresh',
    'common.loading': 'Loading...',
    'common.noData': 'No data',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.close': 'Close',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.all': 'All',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.status': 'Status',
    'common.actions': 'Actions',
    'common.details': 'Details',
    'common.date': 'Date',
    'common.time': 'Time',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.retry': 'Retry',
    'common.error': 'Error occurred',

    'page.selectProject': 'Select Project',
    'page.selectProjectDesc': 'Select a project to manage greenhouses',
    'page.noProject': 'No projects found',
    'page.noProjectDesc': 'You do not have access to any projects',
    'page.noGreenhouse': 'No greenhouses found',
    'page.noGreenhouseDesc': 'This project has no greenhouses',
    'page.loadingGreenhouse': 'Loading greenhouse data...',
    'page.online': 'Online',
    'page.offline': 'Offline',
    'page.ready': 'Ready',
    'page.developing': 'Developing',
    'page.greenhouse': 'Greenhouse',
    'page.loading': 'Loading...',

    'greenhouse.ready': 'Ready',
    'greenhouse.online': 'Online',
    'greenhouse.offline': 'Offline',
    'greenhouse.checking': 'Checking',
    'greenhouse.developing': 'Developing',
    'greenhouse.noDevice': 'No Device ID',

    'dashboard.soilTab': 'Soil',
    'dashboard.chartsTab': 'Charts',
    'dashboard.controlTab': 'Control',
    'dashboard.timersTab': 'Timers',
    'dashboard.temperature': 'Temperature',
    'dashboard.humidity': 'Humidity',
    'dashboard.moisture': 'Soil Moisture',
    'dashboard.co2': 'CO₂',
    'dashboard.light': 'Light',
    'dashboard.online': 'Online',
    'dashboard.offline': 'Offline',
    'dashboard.lastUpdate': 'Last update',
    'dashboard.autoTab': 'Automation', 
    'dashboard.cmdSent': 'Command sent',
    'dashboard.motorCmdSent': 'Motor command sent',
    'dashboard.relayCmdFailed': 'Failed to send command for {name}, please try again',
    'dashboard.motorCmdFailed': 'Failed to send motor command for {name}, please try again',
    'dashboard.noControlPermission': 'You do not have control permission (requires superadmin / operator / admin)',
    'dashboard.autoModeTitle': 'Enable Auto Schedule Mode',
    'dashboard.relayTitle': 'Relay Control',
    'dashboard.motorTitle': 'Shade System',
    'dashboard.shadeAll': 'Shade All 4 Zones',
    'dashboard.unshadeAll': 'Unshade All 4 Zones',
    

    'sensor.air': 'Air',
    'sensor.soil': 'Soil',
    'sensor.water': 'Water',
    'sensor.light': 'Light',
    'sensor.npk': 'NPK Nutrients',
    'sensor.ec': 'EC Value',
    'sensor.ph': 'pH Value',

    'control.on': 'On',
    'control.off': 'Off',
    'control.auto': 'Auto',
    'control.manual': 'Manual',
    'control.fan': 'Fan',
    'control.pump': 'Pump',
    'control.valve': 'Valve',
    'control.light': 'Light',
    'control.motor': 'Motor',
    'control.forward': 'Forward',
    'control.reverse': 'Reverse',
    'control.stop': 'Stop',

    'admin.users': 'Manage Users',
    'admin.projects': 'Manage Projects',
    'admin.greenhouses': 'Manage Greenhouses',
    'admin.sensors': 'Manage Sensors',
    'admin.controls': 'Manage Controls',
    'admin.notifications': 'Notifications',
    'admin.alerts': 'Alert History',
    'admin.audit': 'Audit Log',
    'admin.settings': 'System Settings',
    'admin.security': 'Security',
    'admin.panel': 'Admin Panel',
    'admin.nav.system': 'System',
    'admin.nav.control': 'Control',
    'admin.nav.notify': 'Notifications',
    'admin.nav.settings': 'Settings',
    'admin.nav.backToApp': 'Back to App',
    'admin.nav.automation': 'Automation Rules',
    'admin.nav.scenes': 'Scenes / Presets',
    'admin.nav.controlHistory': 'Control History',


    'admin.user.subtitle': 'Add, edit, and manage user permissions',
    'admin.user.searchPlaceholder': 'Search users...',
    'admin.user.addUser': 'Add User',
    'admin.user.colUser': 'User',
    'admin.user.colRole': 'Role',
    'admin.user.colProject': 'Project',
    'admin.user.allProjects': 'All',
    'admin.user.projectCount': '{n} projects',
    'admin.user.active': 'Active',
    'admin.user.inactive': 'Inactive',
    'admin.user.roleAdmin': 'Admin',
    'admin.user.roleOperator': 'Operator',
    'admin.user.roleViewer': 'Viewer',
    'admin.user.noUser': 'No users found',
    'admin.user.createTitle': 'Add New User',
    'admin.user.editTitle': 'Edit User',
    'admin.user.resetTitle': 'Reset Password',
    'admin.user.fieldUsername': 'Username',
    'admin.user.fieldUsernameHint': 'Used for login (at least 3 characters)',
    'admin.user.fieldUsernamePlaceholder': 'e.g. somchai',
    'admin.user.fieldEmail': 'Email (optional)',
    'admin.user.fieldEmailHint': 'For contact/password recovery (optional)',
    'admin.user.fieldEmailPlaceholder': 'name@example.com',
    'admin.user.fieldPassword': 'Password',
    'admin.user.fieldPasswordHint': 'Recommended: mix letters and numbers for security',
    'admin.user.fieldPasswordPlaceholder': 'At least 6 characters',
    'admin.user.fieldRole': 'Role',
    'admin.user.roleHint': 'Viewer = read only, Operator = control/schedule, Admin = manage users/projects',
    'admin.user.createSuccess': 'User created successfully',
    'admin.user.deleteConfirm': 'Delete user "{name}"?',
    'admin.user.deleteSuccess': 'User deleted successfully',
    'admin.user.noDeleteSuper': 'Cannot delete Super Admin',
    'admin.user.noToggleSuper': 'Cannot enable/disable Super Admin',
    'admin.user.noEditSuper': 'Admin cannot edit Super Admin',
    'admin.user.superLockRole': 'User is Super Admin (role locked) — can only edit username and email',
    'admin.user.activatedMsg': 'User activated',
    'admin.user.deactivatedMsg': 'User deactivated',
    'admin.user.noChange': 'No changes',
    'admin.user.saveSuccess': 'Saved successfully',
    'admin.user.accessTitle': 'Project Access',
    'admin.user.accessDesc': 'Select projects this user can access',
    'admin.user.accessAll': 'User role = {role} has access to "all" (no need to assign per project)',
    'admin.user.accessLoading': 'Loading access...',
    'admin.user.accessSaveSuccess': 'Access saved successfully',
    'admin.user.accessLoadError': 'Failed to load project access',
    'admin.user.superAllAccess': 'Super Admin has full access',
    'admin.user.adminAllAccess': 'Admin already has full access',
    'admin.user.manageAccess': 'Manage project access',
    'admin.user.onlySuperReset': 'Only Super Admin can reset passwords',
    'admin.user.resetPassword': 'Reset Password',
    'admin.user.resetFor': 'User',
    'admin.user.newPassword': 'New Password',
    'admin.user.newPasswordPlaceholder': 'At least 6 characters',
    'admin.user.newPasswordHint': 'Recommended: use letters + numbers for security',
    'admin.user.confirmPassword': 'Confirm New Password',
    'admin.user.confirmPasswordPlaceholder': 'Enter again',
    'admin.user.showPassword': 'Show password',
    'admin.user.hidePassword': 'Hide password',
    'admin.user.resetSuccess': 'Password reset for {name} successful',
    'admin.user.resetBtn': 'Reset',

    'admin.project.subtitle': 'Add, edit, and configure projects',
    'admin.project.searchPlaceholder': 'Search projects...',
    'admin.project.addProject': 'Add Project',
    'admin.project.greenhouse': 'greenhouses',
    'admin.project.deleteConfirm': 'Delete project "{name}"?',
    'admin.project.deleteSuccess': 'Project deleted',
    'admin.project.createSuccess': 'Project created',
    'admin.project.saveSuccess': 'Project saved',
    'admin.project.createTitle': 'Add New Project',
    'admin.project.editTitle': 'Edit Project',
    'admin.project.fieldKey': 'Key',
    'admin.project.fieldName': 'Project Name',
    'admin.project.fieldStatus': 'Status',
    'admin.project.statusDeveloping': 'Developing',
    'admin.project.statusReady': 'Ready',
    'admin.project.tbSection': 'ThingsBoard',
    'admin.project.tbUrl': 'URL',
    'admin.project.tbUrlPlaceholder': 'http://thingsboard:8080',
    'admin.project.tbUsername': 'Username',
    'admin.project.tbPassword': 'Password',
    'admin.project.tbPasswordPlaceholder': '(keep unchanged)',
    'admin.project.noProject': 'No projects found',
    'admin.project.createBtn': 'Create',

    'admin.gh.subtitle': 'Add, edit, and connect ThingsBoard devices',
    'admin.gh.searchPlaceholder': 'Search greenhouses...',
    'admin.gh.allProjects': 'All Projects',
    'admin.gh.addGreenhouse': 'Add Greenhouse',
    'admin.gh.colGreenhouse': 'Greenhouse',
    'admin.gh.colProject': 'Project',
    'admin.gh.colDeviceId': 'Device ID',
    'admin.gh.notConnected': 'Not connected',
    'admin.gh.online': 'Online',
    'admin.gh.offline': 'Offline',
    'admin.gh.statusReady': 'Ready',
    'admin.gh.statusDeveloping': 'Developing',
    'admin.gh.noGreenhouse': 'No greenhouses found',
    'admin.gh.deleteConfirm': 'Delete greenhouse "{name}"?',
    'admin.gh.deleteSuccess': 'Greenhouse deleted',
    'admin.gh.unlinkConfirm': 'Unlink this device?',
    'admin.gh.unlinkSuccess': 'Device unlinked',
    'admin.gh.unlinkTitle': 'Unlink device',
    'admin.gh.linkTitle': 'Link device',
    'admin.gh.createTitle': 'Add New Greenhouse',
    'admin.gh.createSuccess': 'Greenhouse created',
    'admin.gh.fieldProject': 'Project',
    'admin.gh.fieldKey': 'Key (e.g. greenhouse1)',
    'admin.gh.fieldName': 'Greenhouse Name',
    'admin.gh.fieldDeviceId': 'ThingsBoard Device ID (optional)',
    'admin.gh.fieldDeviceIdOptional': 'Optional',
    'admin.gh.createBtn': 'Create',
    'admin.gh.linkDeviceTitle': 'Link Device',
    'admin.gh.linkDeviceHint': 'Copy Device ID from ThingsBoard (Devices → Device Details → Copy device id)',
    'admin.gh.linkSuccess': 'Device linked successfully',
    'admin.gh.linkBtn': 'Link',

    'admin.sensor.subtitle': 'Add and edit dynamic sensors with future expansion support',
    'admin.sensor.selectProject': 'Select project...',
    'admin.sensor.selectProjectHint': 'Select the project to manage',
    'admin.sensor.selectGh': 'Select greenhouse...',
    'admin.sensor.selectGhHint': 'Select the greenhouse to configure sensors',
    'admin.sensor.pleaseSelect': 'Please select a project and greenhouse',
    'admin.sensor.noSensor': 'No sensors — click Template to create a standard set',
    'admin.sensor.colType': 'Type',
    'admin.sensor.colUnit': 'Unit',
    'admin.sensor.colAlert': 'Alert',
    'admin.sensor.deleteConfirm': 'Delete "{name}"?',
    'admin.sensor.deleteSuccess': 'Deleted successfully',
    'admin.sensor.saveSuccess': 'Saved successfully',
    'admin.sensor.createTitle': 'Add Sensor',
    'admin.sensor.editTitle': 'Edit Sensor',
    'admin.sensor.fieldKey': 'Sensor Key (English)',
    'admin.sensor.fieldKeyHint': 'Unique key e.g. air_temp, soil1_moisture — lowercase, underscores, numbers only',
    'admin.sensor.fieldName': 'Sensor Name',
    'admin.sensor.fieldNameHint': 'Display name e.g. Air Temperature',
    'admin.sensor.fieldType': 'Type',
    'admin.sensor.fieldTypeHint': 'Display grouping',
    'admin.sensor.fieldUnit': 'Unit',
    'admin.sensor.fieldUnitHint': 'Unit of measurement',
    'admin.sensor.fieldUnitPlaceholder': '°C, %, ppm',
    'admin.sensor.fieldDataKey': 'Data Key (ThingsBoard)',
    'admin.sensor.fieldDataKeyHint': 'Telemetry key from ThingsBoard — must match what the device sends',
    'admin.sensor.alertMin': 'Alert below',
    'admin.sensor.alertMinHint': 'Leave blank = no alert',
    'admin.sensor.alertMax': 'Alert above',
    'admin.sensor.alertMaxHint': 'Leave blank = no alert',
    'admin.sensor.minValue': 'Gauge min',
    'admin.sensor.minValueHint': 'Leave blank = dashboard default',
    'admin.sensor.maxValue': 'Gauge max',
    'admin.sensor.maxValueHint': 'Leave blank = dashboard default',
    'admin.sensor.calibration': 'Calibration',
    'admin.sensor.calibOffset': 'Offset (+/-)',
    'admin.sensor.calibOffsetHint': 'Value added/subtracted from raw reading',
    'admin.sensor.calibScale': 'Scale (x)',
    'admin.sensor.calibScaleHint': 'Multiplier (1 = unchanged)',
    'admin.sensor.templateTitle': 'Select Template',
    'admin.sensor.templateDesc': 'Auto-create sensor set (skips existing)',
    'admin.sensor.templateSuccess': 'Sensors created successfully',
    'admin.sensor.typeAir': 'Air',
    'admin.sensor.typeSoil': 'Soil',
    'admin.sensor.typeWater': 'Water',
    'admin.sensor.typeLight': 'Light',
    'admin.sensor.typeCustom': 'Other',

    'admin.notif.subtitle': 'Configure Line Notify and alert conditions',
    'admin.notif.lineNotify': 'Line Notify',
    'admin.notif.lineNotifyDesc': 'Receive Line notifications when important events occur',
    'admin.notif.tokenSection': 'Connection Token',
    'admin.notif.currentToken': 'Current token:',
    'admin.notif.newToken': 'New Line Notify Token',
    'admin.notif.tokenUnchanged': '(keep unchanged)',
    'admin.notif.tokenPlaceholder': 'Paste token here',
    'admin.notif.tokenHowTo': 'How to get a token:',
    'admin.notif.tokenStep1': '1. Go to notify-bot.line.me',
    'admin.notif.tokenStep2': '2. Login with your Line account',
    'admin.notif.tokenStep3': '3. Click "Generate Token"',
    'admin.notif.tokenStep4': '4. Select the group or 1-on-1 chat to receive alerts',
    'admin.notif.tokenStep5': '5. Copy the token and paste it here',
    'admin.notif.alertTypes': 'Alert Types',
    'admin.notif.alertOffline': 'Alert when device goes offline',
    'admin.notif.alertOfflineDesc': 'Send message when device loses connection',
    'admin.notif.alertThreshold': 'Alert when value exceeds threshold',
    'admin.notif.alertThresholdDesc': 'Send message when sensor value is abnormal',
    'admin.notif.thresholds': 'Alert Thresholds',
    'admin.notif.tempLabel': 'Air Temperature (°C)',
    'admin.notif.humidityLabel': 'Air Humidity (%)',
    'admin.notif.soilLabel': 'Soil Moisture (%)',
    'admin.notif.min': 'Min',
    'admin.notif.max': 'Max',
    'admin.notif.testBtn': 'Send Test Message',
    'admin.notif.saveBtn': 'Save Settings',
    'admin.notif.saveSuccess': 'Settings saved successfully',
    'admin.notif.testSuccess': 'Test message sent successfully',

    'admin.alert.subtitle': 'View all alerts',
    'admin.alert.statTotal': 'Total',
    'admin.alert.statPending': 'Pending',
    'admin.alert.statCritical': 'Critical',
    'admin.alert.statAcked': 'Acknowledged',
    'admin.alert.allProjects': 'All Projects',
    'admin.alert.allLevels': 'All Levels',
    'admin.alert.allStatus': 'All',
    'admin.alert.pending': 'Unacknowledged',
    'admin.alert.acked': 'Acknowledged',
    'admin.alert.ackedBy': 'by',
    'admin.alert.waitAck': 'Pending',
    'admin.alert.ackBtn': 'Acknowledge',
    'admin.alert.ackSuccess': 'Acknowledged',
    'admin.alert.ackAllConfirm': 'Acknowledge all unacknowledged alerts?',
    'admin.alert.ackAllBtn': 'Acknowledge All',
    'admin.alert.ackAllSuccess': 'All acknowledged',
    'admin.alert.severityCritical': 'Critical',
    'admin.alert.severityWarning': 'Warning',
    'admin.alert.severityInfo': 'Info',
    'admin.alert.colTime': 'Time',
    'admin.alert.colLevel': 'Level',
    'admin.alert.colGreenhouse': 'Greenhouse',
    'admin.alert.colMessage': 'Message',
    'admin.alert.noAlert': 'No alerts',
    'admin.alert.dirAbove': 'above',
    'admin.alert.dirBelow': 'below',
    'admin.alert.showCount': 'Showing {shown} of {total}',

    'admin.audit.subtitle': 'Complete system activity log',
    'admin.audit.statLogs': 'Logs (7 days)',
    'admin.audit.statUsers': 'Users',
    'admin.audit.topActions': 'Top Actions',
    'admin.audit.allActions': 'All Actions',
    'admin.audit.colTime': 'Time',
    'admin.audit.colUser': 'User',
    'admin.audit.colAction': 'Action',
    'admin.audit.colProjectGh': 'Project/GH',
    'admin.audit.colDetail': 'Detail',
    'admin.audit.noLog': 'No logs',
    'admin.audit.system': 'System',
    'admin.audit.showCount': 'Showing {shown} of {total}',
    'admin.audit.exportCsv': 'Export CSV',

    'admin.settings.subtitle': 'View system information and general settings',
    'admin.settings.systemInfo': 'System Info',
    'admin.settings.version': 'Version',
    'admin.settings.nodejs': 'Node.js',
    'admin.settings.database': 'Database',
    'admin.settings.dbMain': 'PostgreSQL Database',
    'admin.settings.dbMainDesc': 'Primary database file',
    'admin.settings.connected': 'Connected',
    'admin.settings.backup': 'Backup',
    'admin.settings.backupEnabled': 'Enabled',
    'admin.settings.backupDisabled': 'Disabled',
    'admin.settings.backupEvery': 'Every {n} days',
    'admin.settings.backupBtn': 'Backup Now',
    'admin.settings.security': 'Security',
    'admin.settings.sessionTimeout': 'Session Timeout',
    'admin.settings.sessionTimeoutValue': '24 hours',
    'admin.settings.auditLog': 'Audit Log',
    'admin.settings.auditLogDesc': 'The system records all user actions such as login, device control, and data edits.',
    'admin.settings.auditLogTip': 'View audit logs from the Audit Log menu or export as CSV.',
    'admin.settings.refresh': 'Refresh',
    'admin.settings.loadError': 'Failed to load data',

    'admin.security.subtitle': 'System security management',
    'admin.security.tabHistory': 'Login History',
    'admin.security.tabSessions': 'Sessions',
    'admin.security.tabIp': 'IP Whitelist',
    'admin.security.statTotal': 'Total Logins',
    'admin.security.statSuccess': 'Successful',
    'admin.security.statFailed': 'Failed',
    'admin.security.statBlocked': 'Blocked',
    'admin.security.statUsers': 'Users',
    'admin.security.colTime': 'Time',
    'admin.security.colUser': 'User',
    'admin.security.colIp': 'IP',
    'admin.security.colStatus': 'Status',
    'admin.security.colLastActive': 'Last Active',
    'admin.security.colExpires': 'Expires',
    'admin.security.colDescription': 'Description',
    'admin.security.colAddedBy': 'Added By',
    'admin.security.colDate': 'Date',
    'admin.security.statusSuccess': 'Success',
    'admin.security.statusFailed': 'Failed',
    'admin.security.statusBlocked': 'Blocked',
    'admin.security.terminateConfirm': 'Terminate this session?',
    'admin.security.terminateSuccess': 'Session terminated',
    'admin.security.terminateBtn': 'Terminate',
    'admin.security.noSession': 'No active sessions',
    'admin.security.ipWhitelist': 'IP Whitelist',
    'admin.security.ipWhitelistDesc': 'Allow only specified IP addresses',
    'admin.security.ipEnabled': 'On',
    'admin.security.ipDisabled': 'Off',
    'admin.security.ipWarning': '⚠️ Warning: If enabled without adding your IP, you will be locked out!',
    'admin.security.addIp': 'Add IP',
    'admin.security.ipPlaceholder': 'IP Address (e.g. 192.168.1.1)',
    'admin.security.ipDescPlaceholder': 'Description',
    'admin.security.addIpSuccess': 'IP added successfully',
    'admin.security.removeIpConfirm': 'Remove this IP?',
    'admin.security.removeIpSuccess': 'IP removed successfully',
    'admin.security.noIp': 'No IPs added yet',
    'admin.security.toggleOnSuccess': 'IP Whitelist enabled',
    'admin.security.toggleOffSuccess': 'IP Whitelist disabled',
    'admin.security.error': 'An error occurred',

    'admin.auto.subtitle': 'Automation rules — if X then Y',
    'admin.auto.selectProject': 'Project',
    'admin.auto.selectProjectPlaceholder': 'Select project...',
    'admin.auto.selectGh': 'Greenhouse',
    'admin.auto.selectGhPlaceholder': 'Select greenhouse...',
    'admin.auto.addRule': 'Add Rule',
    'admin.auto.pleaseSelect': 'Please select a project and greenhouse',
    'admin.auto.noRule': 'No automation rules yet',
    'admin.auto.lastTriggered': 'Last triggered:',
    'admin.auto.triggerCount': '{n} times',
    'admin.auto.toggleSuccess': 'Rule enabled',
    'admin.auto.toggleOffSuccess': 'Rule disabled',
    'admin.auto.deleteConfirm': 'Delete rule "{name}"?',
    'admin.auto.deleteSuccess': 'Deleted successfully',
    'admin.auto.error': 'An error occurred',
    'admin.auto.loadError': 'Failed to load data',
    'admin.auto.triggerSensor': 'When sensor value',
    'admin.auto.triggerTime': 'On schedule',
    'admin.auto.triggerManual': 'Manual',
    'admin.auto.condAbove': 'Above',
    'admin.auto.condBelow': 'Below',
    'admin.auto.condEqual': 'Equal to',
    'admin.auto.condBetween': 'Between',
    'admin.auto.timeAt': 'At',
    'admin.auto.actionsCount': '{n} Actions',
    'admin.auto.createTitle': 'Create New Rule',
    'admin.auto.editTitle': 'Edit Rule',
    'admin.auto.fieldName': 'Rule Name',
    'admin.auto.fieldNamePlaceholder': 'e.g. Turn on fan when hot',
    'admin.auto.fieldDesc': 'Description (optional)',
    'admin.auto.fieldDescPlaceholder': 'Additional details',
    'admin.auto.triggerType': 'Trigger Type',
    'admin.auto.sensorKey': 'Sensor Key',
    'admin.auto.sensorKeyPlaceholder': 'e.g. air_temp',
    'admin.auto.condition': 'Condition',
    'admin.auto.value': 'Value',
    'admin.auto.time': 'Time',
    'admin.auto.actions': 'Actions (what to do)',
    'admin.auto.addAction': 'Add Action',
    'admin.auto.actionControl': 'Control Device',
    'admin.auto.actionNotif': 'Send Notification',
    'admin.auto.controlKeyPlaceholder': 'Control Key (e.g. fan_1)',
    'admin.auto.actionOn': 'On',
    'admin.auto.actionOff': 'Off',
    'admin.auto.actionToggle': 'Toggle',
    'admin.auto.messagePlaceholder': 'Message',
    'admin.auto.saveSuccess': 'Saved successfully',

    'admin.scene.subtitle': 'Preset command sets e.g. Morning, Day, Night',
    'admin.scene.selectProject': 'Project',
    'admin.scene.selectProjectPlaceholder': 'Select project...',
    'admin.scene.selectGh': 'Greenhouse',
    'admin.scene.selectGhPlaceholder': 'Select greenhouse...',
    'admin.scene.addScene': 'Create Scene',
    'admin.scene.pleaseSelect': 'Please select a project and greenhouse',
    'admin.scene.noScene': 'No scenes yet',
    'admin.scene.executeSuccess': '"{name}" executed successfully',
    'admin.scene.deleteConfirm': 'Delete scene "{name}"?',
    'admin.scene.deleteSuccess': 'Deleted successfully',
    'admin.scene.error': 'An error occurred',
    'admin.scene.loadError': 'Failed to load data',
    'admin.scene.executeBtn': 'Run',
    'admin.scene.iconLabel': 'Icon',
    'admin.scene.iconLightning': 'Lightning',
    'admin.scene.iconDay': 'Day',
    'admin.scene.iconNight': 'Night',
    'admin.scene.iconMorning': 'Morning',
    'admin.scene.colorLabel': 'Color',
    'admin.scene.createTitle': 'Create New Scene',
    'admin.scene.editTitle': 'Edit Scene',
    'admin.scene.fieldName': 'Scene Name',
    'admin.scene.fieldNamePlaceholder': 'e.g. Morning, Day, Night',
    'admin.scene.fieldDesc': 'Description',
    'admin.scene.actions': 'Actions',
    'admin.scene.actionOn': 'On',
    'admin.scene.actionOff': 'Off',
    'admin.scene.saveSuccess': 'Saved successfully',

    'admin.ctrl.subtitle': 'Complete device on/off control log',
    'admin.ctrl.selectProject': 'Project',
    'admin.ctrl.selectProjectPlaceholder': 'Select project...',
    'admin.ctrl.selectGh': 'Greenhouse',
    'admin.ctrl.selectGhPlaceholder': 'Select greenhouse...',
    'admin.ctrl.loadBtn': 'Load History',
    'admin.ctrl.pleaseSelect': 'Please select a project and greenhouse',
    'admin.ctrl.statTotal': 'Total',
    'admin.ctrl.statManual': 'Manual',
    'admin.ctrl.statSchedule': 'Scheduled',
    'admin.ctrl.statAuto': 'Automation',
    'admin.ctrl.srcManual': 'Manual',
    'admin.ctrl.srcSchedule': 'Schedule',
    'admin.ctrl.srcAuto': 'Automation',
    'admin.ctrl.srcScene': 'Scene',
    'admin.ctrl.colTime': 'Time',
    'admin.ctrl.colDevice': 'Device',
    'admin.ctrl.colAction': 'Action',
    'admin.ctrl.colSource': 'Source',
    'admin.ctrl.colUser': 'User',
    'admin.ctrl.actionOn': 'On',
    'admin.ctrl.actionOff': 'Off',
    'admin.ctrl.noHistory': 'No history yet',
    'admin.ctrl.loadError': 'Failed to load data',

    'role.superadmin': 'Super Admin',
    'role.admin': 'Admin',
    'role.operator': 'Operator',
    'role.viewer': 'Viewer',

    'msg.success': 'Success',
    'msg.error': 'Error occurred',
    'msg.saved': 'Saved successfully',
    'msg.deleted': 'Deleted successfully',
    'msg.confirmDelete': 'Confirm delete?',
    'msg.noPermission': 'You do not have permission',

    'profile.title': 'Profile',
    'profile.subtitle': 'Manage your profile and preferences',
    'profile.lastLogin': 'Last login:',
    'profile.createdAt': 'Created at:',
    'profile.changePassword': 'Change password',
    'profile.currentPassword': 'Current password',
    'profile.newPassword': 'New password',
    'profile.confirmPassword': 'Confirm new password',
    'profile.passwordMismatch': 'New passwords do not match',
    'profile.passwordMin6': 'Password must be at least 6 characters',
    'profile.passwordChanged': 'Password changed successfully',
    'profile.preferencesTitle': 'Preferences',
    'profile.language': 'Language',
    'profile.theme': 'Theme',
    'profile.themeLight': '☀️ Light',
    'profile.themeDark': '🌙 Dark',
    'profile.themeSystem': '💻 System',
    'profile.savePreferences': 'Save settings',
    'profile.preferencesSaved': 'Preferences saved',
    'profile.strengthWeak': 'Weak',
    'profile.strengthMedium': 'Medium',
    'profile.strengthStrong': 'Strong',

    'agri.title': 'Agriculture',
    'agri.subtitle': 'Select a category to manage',
    'agri.menu.crops': 'Manage Crops',
    'agri.menu.cropsDesc': 'Record and track planted crops',
    'agri.menu.growth': 'Growth Records',
    'agri.menu.growthDesc': 'Track plant growth progress',
    'agri.menu.fertilizer': 'Fertilizer Schedule',
    'agri.menu.fertilizerDesc': 'Plan and track fertilization',
    'agri.menu.pestDisease': 'Pest & Disease',
    'agri.menu.pestDiseaseDesc': 'Record and track pest and disease issues',
    'agri.menu.yield': 'Yield',
    'agri.menu.yieldDesc': 'Track harvests and revenue',
    'agri.menu.water': 'Water Usage',
    'agri.menu.waterDesc': 'Track water volume and costs',


    'agri.crop.status.planted': 'Planted',
    'agri.crop.status.growing': 'Growing',
    'agri.crop.status.harvested': 'Harvested',
    'agri.crop.status.failed': 'Failed',
    'agri.crop.statTotal': 'Total Crops',
    'agri.crop.statNearHarvest': 'Near Harvest (7d)',
    'agri.crop.addCrop': 'Add Crop',
    'agri.crop.addTitle': 'Add New Crop',
    'agri.crop.editTitle': 'Edit Crop',
    'agri.crop.confirmDelete': 'Delete',
    'agri.crop.confirmHarvest': 'Record harvest for',
    'agri.crop.noData': 'No crop data yet',
    'agri.crop.colName': 'Crop',
    'agri.crop.colPlantDate': 'Plant Date',
    'agri.crop.colExpectedHarvest': 'Expected Harvest',
    'agri.crop.variety': 'Variety',
    'agri.crop.daysLeft': '{n} days left',
    'agri.crop.fieldName': 'Crop Name',
    'agri.crop.fieldNamePlaceholder': 'e.g. Lettuce',
    'agri.crop.fieldVariety': 'Variety',
    'agri.crop.fieldVarietyPlaceholder': 'e.g. Red Oak',
    'agri.col.greenhouse': 'Greenhouse',
    'agri.col.quantity': 'Quantity',
    'agri.col.unit': 'Unit',
    'agri.filter.allProjects': 'All Projects',
    'agri.filter.allGreenhouses': 'All Greenhouses',
    'agri.filter.allStatus': 'All Status',
    'agri.label.project': 'Project',
    'agri.label.greenhouse': 'Greenhouse',
    'agri.label.select': 'Select...',
    'agri.label.notes': 'Notes',
    'agri.label.notesPlaceholder': 'Additional details...',
    'agri.unit.plant': 'Plant',
    'agri.unit.tray': 'Tray',
    'agri.unit.plot': 'Plot',
    'agri.unit.kg': 'kg',

    'agri.fertilizer.overdue': 'Overdue',
    'agri.fertilizer.today': 'Today',
    'agri.fertilizer.upcoming': 'Upcoming',
    'agri.fertilizer.completed': 'Completed',
    'agri.fertilizer.showCompleted': 'Show completed',
    'agri.fertilizer.addSchedule': 'Add Schedule',
    'agri.fertilizer.addTitle': 'Add Fertilizer Schedule',
    'agri.fertilizer.editTitle': 'Edit Fertilizer Schedule',
    'agri.fertilizer.addSuccess': 'Schedule added',
    'agri.fertilizer.completeSuccess': 'Marked as completed',
    'agri.fertilizer.completedAt': 'Done',
    'agri.fertilizer.confirmDelete': 'Delete',
    'agri.fertilizer.noData': 'No fertilizer schedules yet',
    'agri.fertilizer.fieldName': 'Fertilizer Name',
    'agri.fertilizer.fieldNamePlaceholder': 'e.g. NPK 16-16-16',
    'agri.fertilizer.fieldType': 'Type',
    'agri.fertilizer.fieldDate': 'Date',
    'agri.fertilizer.fieldAmount': 'Amount',
    'agri.fertilizer.typeChemical': 'Chemical',
    'agri.fertilizer.typeOrganic': 'Organic',
    'agri.fertilizer.typeBio': 'Bio',
    'agri.fertilizer.typeFoliar': 'Foliar',

    'agri.growth.health.excellent': 'Excellent',
    'agri.growth.health.good': 'Good',
    'agri.growth.health.fair': 'Fair',
    'agri.growth.health.poor': 'Poor',
    'agri.growth.selectCrop': 'Select Crop',
    'agri.growth.selectCropPlaceholder': 'Select crop to track...',
    'agri.growth.addRecord': 'Add Record',
    'agri.growth.addTitle': 'Add Growth Record',
    'agri.growth.editTitle': 'Edit Record',
    'agri.growth.pleaseSelect': 'Please select a crop',
    'agri.growth.confirmDelete': 'Delete this record?',
    'agri.growth.noData': 'No records yet',
    'agri.growth.totalGrowth': 'Total Growth',
    'agri.growth.avgRate': 'Average Growth Rate',
    'agri.growth.recordCount': 'Record Count',
    'agri.growth.day': 'day',
    'agri.growth.colDate': 'Date',
    'agri.growth.colHeight': 'Height',
    'agri.growth.colLeafCount': 'Leaf Count',
    'agri.growth.colHealth': 'Health',
    'agri.growth.colPhoto': 'Photo',
    'agri.growth.colRecordedBy': 'Recorded By',
    'agri.growth.viewPhoto': 'View Photo',
    'agri.growth.fieldHeight': 'Height (cm)',
    'agri.growth.fieldLeafCount': 'Leaf Count',
    'agri.growth.fieldPhotoUrl': 'Photo URL',

    'agri.pest.title': 'Pest & Disease',
    'agri.pest.subtitle': 'Record and track pest and disease issues',
    'agri.pest.typePest': 'Pest',
    'agri.pest.typeDisease': 'Disease',
    'agri.pest.typePestShort': 'Pest',
    'agri.pest.typeDiseaseShort': 'Disease',
    'agri.pest.severity.low': 'Low',
    'agri.pest.severity.medium': 'Medium',
    'agri.pest.severity.high': 'High',
    'agri.pest.severity.critical': 'Critical',
    'agri.pest.statCritical': 'High Severity',
    'agri.pest.statResolved': 'Resolved',
    'agri.pest.filterAll': 'All Types',
    'agri.pest.showResolved': 'Show resolved',
    'agri.pest.addRecord': 'Record Issue',
    'agri.pest.addTitle': 'Record Pest/Disease',
    'agri.pest.editTitle': 'Edit Record',
    'agri.pest.confirmDelete': 'Delete this record?',
    'agri.pest.resolved': 'Resolved',
    'agri.pest.markResolved': 'Mark Resolved',
    'agri.pest.resolveSuccess': 'Marked as resolved',
    'agri.pest.fieldType': 'Type',
    'agri.pest.fieldName': 'Pest/Disease Name',
    'agri.pest.fieldNamePlaceholder': 'e.g. Aphids, Powdery Mildew',
    'agri.pest.fieldSeverity': 'Severity',
    'agri.pest.fieldAffectedArea': 'Affected Area',
    'agri.pest.fieldAffectedAreaPlaceholder': 'e.g. Plot 1-3',
    'agri.pest.fieldTreatment': 'Treatment',
    'agri.pest.fieldTreatmentPlaceholder': 'e.g. Spray bio-pesticide',

    'agri.water.statTotalM3': 'Total (m³)',
    'agri.water.statAvgPerDay': 'Avg/day (L)',
    'agri.water.statTotalCost': 'Total Cost',
    'agri.water.statPerM3': 'Per m³',
    'agri.water.chartTitle': 'Water Usage',
    'agri.water.addRecord': 'Record Water Usage',
    'agri.water.addTitle': 'Record Water Usage',
    'agri.water.editTitle': 'Edit Record',
    'agri.water.confirmDelete': 'Delete this record?',
    'agri.water.noData': 'No data yet',
    'agri.water.colUsage': 'Usage (L)',
    'agri.water.colSource': 'Source',
    'agri.water.colCost': 'Cost',
    'agri.water.fieldUsage': 'Usage (Liters)',
    'agri.water.fieldCost': 'Cost (฿)',
    'agri.water.source.tap': 'Tap Water',
    'agri.water.source.well': 'Well Water',
    'agri.water.source.rain': 'Rainwater',
    'agri.water.source.river': 'Natural Source',

    'agri.yield.gradeA': 'Grade A',
    'agri.yield.gradeB': 'Grade B',
    'agri.yield.gradeC': 'Grade C',
    'agri.yield.gradeReject': 'Reject',
    'agri.yield.statHarvestCount': 'Harvest Count',
    'agri.yield.statTotalYield': 'Total Yield (kg)',
    'agri.yield.statTotalRevenue': 'Total Revenue',
    'agri.yield.statAvgPrice': 'Avg Price/kg',
    'agri.yield.chartTitle': 'Monthly Yield',
    'agri.yield.chartQuantity': 'Quantity (kg)',
    'agri.yield.chartRevenue': 'Revenue (฿)',
    'agri.yield.addRecord': 'Record Yield',
    'agri.yield.addTitle': 'Record Yield',
    'agri.yield.editTitle': 'Edit Record',
    'agri.yield.confirmDelete': 'Delete this record?',
    'agri.yield.noData': 'No data yet',
    'agri.yield.colCrop': 'Crop',
    'agri.yield.colQuantity': 'Quantity',
    'agri.yield.colGrade': 'Grade',
    'agri.yield.colPricePerUnit': 'Price/Unit',
    'agri.yield.colRevenue': 'Revenue',
    'agri.yield.fieldHarvestDate': 'Harvest Date',
    'agri.yield.fieldPricePerUnit': 'Price per unit (฿)',
    'agri.yield.totalRevenue': 'Total Revenue',
    'agri.yield.unitKg': 'Kilogram (kg)',
    'agri.yield.unitG': 'Gram (g)',
    'agri.yield.unitPcs': 'Pieces',
    'agri.yield.unitBunch': 'Bunch',

    'soil.airSection': 'Air Sensors',
    'soil.soilSection': 'Soil Sensors (10 nodes)',
    'soil.systemSection': 'System Info',

    'charts.range1h': '1 Hour',
    'charts.range6h': '6 Hours',
    'charts.range24h': '24 Hours',
    'charts.range7d': '7 Days',
    'charts.range30d': '30 Days',
    'charts.rangeCustom': 'Custom',
    'charts.soilMoisture': 'Moisture',
    'charts.soilTemp': 'Temperature',
    'charts.errSelectDate': 'Please select start and end dates',
    'charts.errStartBeforeEnd': 'Start date must be before end date',
    'charts.errMax90Days': 'Maximum range is 90 days',
    'charts.errSelectRange': 'Please select a time range',
    'charts.loadError': 'Failed to load chart data',
    'charts.notReady': 'This greenhouse is not ready to display charts',
    'charts.airChartTitle': 'Air Sensor Chart',
    'charts.soilChartTitle': 'Soil Sensor Chart',
    'charts.fullscreen': 'Fullscreen',
    'charts.allNodes': 'All 10 Nodes',
    'charts.selectNode': 'Select Node',
    'charts.show': 'Show',
    'charts.params': 'Parameters',
    'charts.nodeLabel': 'Node',
    'charts.node': 'Node',
    'charts.timeRange': 'Time Range',
    'charts.customRangeTitle': 'Custom Range (max 90 days)',
    'charts.startDate': 'Start Date',
    'charts.endDate': 'End Date',
    'charts.showingData': 'Showing data',
    'charts.to': 'to',

    'timers.fan1': 'Main Fan',
    'timers.fan2': 'Circulation Fan',
    'timers.valve1': 'Water Zone 1',
    'timers.valve2': 'Water Zone 2',
    'timers.valve3': 'Water Zone 3',
    'timers.valve4': 'Water Zone 4',
    'timers.light1': 'Supplemental Light',
    'timers.motor': 'Motor (All)',
    'timers.saveSuccess': 'Timer saved successfully',
    'timers.invalidFormat': 'Invalid time format (HH:mm)',
    'timers.noPermission': 'You do not have permission to set timers (requires superadmin / operator / admin)',
    'timers.infoNote': '⏰ Auto on/off schedule (Auto mode must also be enabled in the Control tab)',
    'timers.onTime': 'On Time',
    'timers.offTime': 'Off Time',
    'timers.motorDesc': 'Set movement time (Forward/Reverse)',
    'timers.forwardTime': 'Forward Time (Down)',
    'timers.reverseTime': 'Reverse Time (Up)',

    'auto.water': 'Water (4 Zones)',
    'auto.motorShade': 'Shade System (4 Zones)',
    'auto.sensorAirTemp': 'Air Temperature',
    'auto.sensorAirHumidity': 'Air Humidity',
    'auto.sensorLight': 'Light',
    'auto.sensorCo2': 'CO2',
    'auto.sensorSoilMoisture': 'Soil Moisture Node {n}',
    'auto.condGt': 'Greater than (>)',
    'auto.condLt': 'Less than (<)',
    'auto.condGte': 'Greater than or equal (≥)',
    'auto.condLte': 'Less than or equal (≤)',
    'auto.modeDaily': 'Daily Schedule',
    'auto.modeCondition': 'Condition-based',
    'auto.modeCycle': 'Cycle Timer',
    'auto.title': 'Auto System',
    'auto.subtitle': 'Smart automation with 3 intelligent systems',
    'auto.overview': 'Overview',
    'auto.configMode': 'Configure Auto Mode',
    'auto.cardDaily': 'On/Off Schedule',
    'auto.cardCondition': 'Condition-based Control',
    'auto.cardCycle': 'Cycle Operation',
    'auto.realtimeStatus': 'Real-time Status',
    'auto.updating': 'Updating...',
    'auto.working': 'Working',
    'auto.idle': 'Idle',
    'auto.controlSystem': 'Control System',
    'auto.deviceOffline': 'Device Offline',
    'auto.offlineDesc': 'Automation will run based on the last saved settings on the device',

    'air.staleData': 'Data not updated',
    'air.outOfRange': 'Value out of range',
    'air.temperature': 'Temperature',
    'air.tempWarning': 'Temperature abnormal',
    'air.humidity': 'Humidity',
    'air.humidityWarning': 'Humidity abnormal',
    'air.co2Warning': 'CO₂ too high',
    'air.light': 'Light',

    'autoDevice.sequential': 'Sequential 4-Zone System',
    'autoDevice.allZones': 'All 4 Zones Together',
    'autoDevice.autoControl': 'Auto Control',
    'autoDevice.currentMode': 'Current Mode',
    'autoDevice.unknown': 'Unknown',
    'autoDevice.tabSchedule': 'Schedule',
    'autoDevice.tabCondition': 'Condition',
    'autoDevice.tabCycle': 'Cycle',
    'autoDevice.dailyInfo': 'Simple on/off schedule, repeats daily',
    'autoDevice.dailyGoTo': 'Configure in the "Timers" tab for each device',
    'autoDevice.enable': 'Enable',
    'autoDevice.conditionDesc': 'Control based on sensor condition',
    'autoDevice.selectSensor': 'Select Sensor',
    'autoDevice.condition': 'Condition',
    'autoDevice.targetValue': 'Target Value',
    'autoDevice.whenTrue': 'When condition is true',
    'autoDevice.actionForward': 'Forward',
    'autoDevice.actionReverse': 'Reverse',
    'autoDevice.actionOn': 'On',
    'autoDevice.actionOff': 'Off',
    'autoDevice.exampleTitle': 'Example',
    'autoDevice.saving': 'Saving...',
    'autoDevice.saveBtn': 'Save Settings',
    'autoDevice.cycleDesc': 'Cycle operation',
    'autoDevice.timeRange': 'Operating Time Range',
    'autoDevice.start': 'Start',
    'autoDevice.end': 'End',
    'autoDevice.onTimePerZone': 'Time per zone (min)',
    'autoDevice.onTime': 'On time (min)',
    'autoDevice.waitBetween': 'Wait between cycles (min)',
    'autoDevice.offTime': 'Off time (min)',
    'autoDevice.maxCycles': 'Max Cycles',
    'autoDevice.maxCyclesHint': '1-10 cycles allowed',
    'autoDevice.exampleCycle1': 'Example (Cycle 1):',
    'autoDevice.zone1': 'Open water zone 1',
    'autoDevice.zone2': 'Open water zone 2',
    'autoDevice.zone3': 'Open water zone 3',
    'autoDevice.zone4': 'Open water zone 4',
    'autoDevice.cycleWait': 'Wait {off} min then repeat (max {max} cycles)',
    'autoDevice.cycleWarning': '⚠️ If near end time, system completes all 4 zones before stopping',
    'autoDevice.cycleExample': 'Time {start}-{end} → On {on} min, Off {off} min, Repeat {max} cycles',

    'autoMode.title': 'Auto Schedule Mode',
    'autoMode.subtitle': 'Enable/disable automatic timer-based control',
    'autoMode.infoText': 'When auto mode is on, devices will run according to the set timers',
    'autoMode.on': 'On',
    'autoMode.off': 'Off',
    'autoMode.fan1': 'Main Fan',
    'autoMode.fan2': 'Circulation Fan',
    'autoMode.valve1': 'Water Zone 1',
    'autoMode.valve2': 'Water Zone 2',
    'autoMode.valve3': 'Water Zone 3',
    'autoMode.valve4': 'Water Zone 4',
    'autoMode.light1': 'Supplemental Light',
    'autoMode.motor': 'All Motors',

    'banner.developingTitle': 'Under Development',
    'banner.developingDefault': 'No ThingsBoard Device linked (deviceId)',
    'banner.developingNote': 'All values will show -- and all controls will be locked',
    'banner.offlineTitle': 'Device Offline',
    'banner.offlineDesc': 'Cannot connect to device. All controls will be locked',

    'export.title': 'Export Data',
    'export.format': 'File Format',
    'export.dateRange': 'Date Range',
    'export.last7': 'Last 7 days',
    'export.last30': 'Last 30 days',
    'export.last90': 'Last 90 days',
    'export.startDate': 'Start Date',
    'export.endDate': 'End Date',
    'export.maxNote': '* Maximum range is 1 year',
    'export.airSensors': 'Air Sensors',
    'export.soilNodes': 'Soil Nodes',
    'export.soilNode': 'Node {n}',
    'export.soilParams': 'Soil Parameters',
    'export.downloadBtn': 'Download',
    'export.downloading': 'Downloading...',
    'export.btnLabel': 'Export',
    'export.successMsg': 'Download successful ({n} days)',
    'export.errNoDate': 'Please select start and end dates',
    'export.errStartEnd': 'Start date must be before end date',
    'export.errMax1Year': 'Maximum export range is 1 year',
    'export.errNoSensor': 'Please select at least 1 sensor',
    'export.errFailed': 'Export failed',

    'exportSimple.selectRange': 'Select Range',
    'exportSimple.days': '{n} days',
    'exportSimple.successMsg': 'Downloaded {n} days of data successfully',

    'motor.notReady': 'Not Ready',
    'motor.shading': '🌥️ Shading',
    'motor.notShading': '☀️ Not Shading',
    'motor.stopped': '⏸️ Stopped',
    'motor.shade': 'Shade',
    'motor.unshade': 'Unshade',

    'relay.working': 'Running',
    'relay.idle': 'Idle',

    'report.title': 'Download PDF Report',
    'report.subtitle': 'Sensor data summary and statistics',
    'report.period1d': 'Daily',
    'report.period1dDesc': 'Last 24 hours',
    'report.period7d': 'Weekly',
    'report.period7dDesc': 'Last 7 days',
    'report.period30d': 'Monthly',
    'report.period30dDesc': 'Last 30 days',
    'report.shareTitle': 'Save or Share Report',
    'report.successMsg': 'Report downloaded successfully',
    'report.errFailed': 'Failed to download report',
    'report.downloadBtn': 'Download Report',

    'soil.nutrients': 'Nutrients (mg/kg)',

    'sysinfo.signal': 'Signal',
    'sysinfo.readTime': 'Read Time',
    'sysinfo.sigUnknown': 'Unknown',
    'sysinfo.sigExcellent': 'Excellent',
    'sysinfo.sigGood': 'Good',
    'sysinfo.sigFair': 'Fair',
    'sysinfo.sigWeak': 'Weak',
    'sysinfo.sigVeryWeak': 'Very Weak',

    'weather.unavailable': 'Weather data unavailable',
    'weather.updated': 'Updated at',
    'weather.data_at': 'Data at',
    'weather.cached': 'cached',
    'weather.sectionTitle': 'Outdoor Weather',
    'admin.weather': 'Weather Settings',
    'admin.weather.subtitle': 'Configure location and display fields for each greenhouse',
    'admin.weather.searchPlaceholder': 'Search location...',
    'admin.weather.searchBtn': 'Search',
    'admin.weather.selectLocation': 'Select Location',
    'admin.weather.currentLocation': 'Current Location',
    'admin.weather.latitude': 'Latitude',
    'admin.weather.longitude': 'Longitude',
    'admin.weather.showFields': 'Display Fields',
    'admin.weather.showTemperature': 'Temperature',
    'admin.weather.showHumidity': 'Humidity',
    'admin.weather.showCondition': 'Condition',
    'admin.weather.showWindSpeed': 'Wind Speed',
    'admin.weather.showUvIndex': 'UV Index',
    'admin.weather.showRainChance': 'Rain Chance',
    'admin.weather.saveSuccess': 'Settings saved successfully',
    'admin.weather.noConfig': 'No config yet (default: Bangkok)',
    'admin.calibrate': 'Calibrate Sensor',

  },

  mm: {
    'app.title': 'GreenHouse Pro',
    'app.subtitle': 'စမတ်ဖန်လုံအိမ်စီမံခန့်ခွဲမှုစနစ်',

    'project.locked': 'သော့ခတ်ထား',
    'project.viewGreenhouses': 'ဖန်လုံအိမ်များကြည့်ရန်',
    'project.noAccess': 'သင့်တွင်ဤပရောဂျက်အတွက် ခွင့်ပြုချက်မရှိပါ',

    'nav.home': 'ပင်မစာမျက်နှာ',
    'nav.agriculture': 'စိုက်ပျိုးရေး',
    'nav.dashboard': 'ဒက်ရှ်ဘုတ်',
    'nav.projects': 'ပရောဂျက်များ',
    'nav.greenhouses': 'ဖန်လုံအိမ်များ',
    'nav.admin': 'အက်ဒမင်',
    'nav.profile': 'ပရိုဖိုင်',
    'nav.settings': 'ဆက်တင်များ',
    'nav.logout': 'ထွက်ရန်',
    'nav.notifications': 'အကြောင်းကြားချက်',

    'auth.login': 'ဝင်ရောက်ရန်',
    'auth.logout': 'ထွက်ရန်',
    'auth.username': 'အသုံးပြုသူအမည်',
    'auth.password': 'စကားဝှက်',
    'auth.remember': 'မှတ်ထားပါ',
    'auth.forgot': 'စကားဝှက်မေ့နေပါသလား?',

    'common.save': 'သိမ်းဆည်းရန်',
    'common.cancel': 'ပယ်ဖျက်ရန်',
    'common.delete': 'ဖျက်ရန်',
    'common.edit': 'တည်းဖြတ်ရန်',
    'common.add': 'ထည့်ရန်',
    'common.search': 'ရှာရန်',
    'common.filter': 'စစ်ထုတ်ရန်',
    'common.refresh': 'ပြန်လည်စတင်ရန်',
    'common.loading': 'ဖွင့်နေသည်...',
    'common.noData': 'ဒေတာမရှိပါ',
    'common.confirm': 'အတည်ပြုရန်',
    'common.back': 'နောက်သို့',
    'common.next': 'ရှေ့သို့',
    'common.previous': 'အရင်',
    'common.close': 'ပိတ်ရန်',
    'common.export': 'ထုတ်ယူရန်',
    'common.import': 'သွင်းယူရန်',
    'common.download': 'ဒေါင်းလုဒ်',
    'common.upload': 'အပ်လုဒ်',
    'common.all': 'အားလုံး',
    'common.active': 'အသုံးပြုနေသည်',
    'common.inactive': 'မသုံးပါ',
    'common.status': 'အခြေအနေ',
    'common.actions': 'လုပ်ဆောင်ချက်များ',
    'common.details': 'အသေးစိတ်',
    'common.date': 'ရက်စွဲ',
    'common.time': 'အချိန်',
    'common.yes': 'ဟုတ်ကဲ့',
    'common.no': 'မဟုတ်ပါ',
    'common.retry': 'ထပ်စမ်းကြည့်ပါ',
    'common.error': 'အမှားဖြစ်သည်',

    'page.selectProject': 'ပရောဂျက်ရွေးပါ',
    'page.selectProjectDesc': 'ဖန်လုံအိမ်စီမံရန် ပရောဂျက်ရွေးပါ',
    'page.noProject': 'ပရောဂျက်မတွေ့ပါ',
    'page.noProjectDesc': 'သင့်တွင် ပရောဂျက်ခွင့်ပြုချက်မရှိပါ',
    'page.noGreenhouse': 'ဖန်လုံအိမ်မတွေ့ပါ',
    'page.noGreenhouseDesc': 'ဤပရောဂျက်တွင် ဖန်လုံအိမ်မရှိပါ',
    'page.loadingGreenhouse': 'ဖန်လုံအိမ်ဒေတာ ဖွင့်နေသည်...',
    'page.online': 'အွန်လိုင်း',
    'page.offline': 'အော့ဖ်လိုင်း',
    'page.ready': 'အသုံးပြုရန်အဆင်သင့်',
    'page.developing': 'တည်ဆောက်နေဆဲ',
    'page.greenhouse': 'ဖန်လုံအိမ်',
    'page.loading': 'ဖွင့်နေသည်...',

    'greenhouse.ready': 'အသုံးပြုရန်အဆင်သင့်',
    'greenhouse.online': 'အွန်လိုင်း',
    'greenhouse.offline': 'အော့ဖ်လိုင်း',
    'greenhouse.checking': 'စစ်ဆေးနေသည်',
    'greenhouse.developing': 'တည်ဆောက်နေဆဲ',
    'greenhouse.noDevice': 'Device ID မရှိပါ',

    'dashboard.soilTab': 'မြေဆီ',
    'dashboard.chartsTab': 'ဇယားများ',
    'dashboard.controlTab': 'ထိန်းချုပ်',
    'dashboard.timersTab': 'အချိန်ဇယား',
    'dashboard.temperature': 'အပူချိန်',
    'dashboard.humidity': 'စိုထိုင်းဆ',
    'dashboard.moisture': 'မြေစိုထိုင်းဆ',
    'dashboard.co2': 'CO₂',
    'dashboard.light': 'အလင်း',
    'dashboard.online': 'အွန်လိုင်း',
    'dashboard.offline': 'အော့ဖ်လိုင်း',
    'dashboard.lastUpdate': 'နောက်ဆုံးအပ်ဒိတ်',
    'dashboard.autoTab': 'အလိုအလျောက်',
    'dashboard.cmdSent': 'အမိန့်ပို့ပြီးပါပြီ',
    'dashboard.motorCmdSent': 'မော်တာအမိန့်ပို့ပြီးပါပြီ',
    'dashboard.relayCmdFailed': '{name} အမိန့်ပို့မရပါ၊ ထပ်စမ်းပါ',
    'dashboard.motorCmdFailed': '{name} မော်တာအမိန့်ပို့မရပါ၊ ထပ်စမ်းပါ',
    'dashboard.noControlPermission': 'ထိန်းချုပ်ခွင့်မရှိပါ (superadmin / operator / admin လိုသည်)',
    'dashboard.autoModeTitle': 'အလိုအလျောက်အချိန်ဇယားမုဒ် ဖွင့်ရန်',
    'dashboard.relayTitle': 'ရီလေးထိန်းချုပ်မှု',
    'dashboard.motorTitle': 'အရိပ်ကာစနစ်',
    'dashboard.shadeAll': 'ဇုန် ၄ ခုလုံး အရိပ်ကာရန်',
    'dashboard.unshadeAll': 'ဇုန် ၄ ခုလုံး အရိပ်ဖယ်ရန်',

    'sensor.air': 'လေ',
    'sensor.soil': 'မြေ',
    'sensor.water': 'ရေ',
    'sensor.light': 'အလင်း',
    'sensor.npk': 'NPK အာဟာရဓာတ်',
    'sensor.ec': 'EC တန်ဖိုး',
    'sensor.ph': 'pH တန်ဖိုး',

    'control.on': 'ဖွင့်',
    'control.off': 'ပိတ်',
    'control.auto': 'အလိုအလျောက်',
    'control.manual': 'လက်ဖြင့်',
    'control.fan': 'ပန်ကာ',
    'control.pump': 'ရေမောင်း',
    'control.valve': 'ဗားဗ်',
    'control.light': 'မီး',
    'control.motor': 'မော်တာ',
    'control.forward': 'ရှေ့တိုး',
    'control.reverse': 'နောက်ဆုတ်',
    'control.stop': 'ရပ်',

    'role.superadmin': 'အထူးအက်ဒမင်',
    'role.admin': 'အက်ဒမင်',
    'role.operator': 'အော်ပရေတာ',
    'role.viewer': 'ကြည့်ရှုသူ',

    'msg.success': 'အောင်မြင်သည်',
    'msg.error': 'အမှားဖြစ်သည်',
    'msg.saved': 'သိမ်းဆည်းပြီးပါပြီ',
    'msg.deleted': 'ဖျက်ပြီးပါပြီ',
    'msg.confirmDelete': 'ဖျက်ရန်အတည်ပြုပါသလား?',
    'msg.noPermission': 'သင့်တွင်ခွင့်ပြုချက်မရှိပါ',

    'admin.users': 'အသုံးပြုသူများ စီမံပါ',
    'admin.projects': 'ပရောဂျက်များ စီမံပါ',
    'admin.greenhouses': 'ဖန်လုံအိမ်များ စီမံပါ',
    'admin.sensors': 'Sensor များ စီမံပါ',
    'admin.controls': 'ထိန်းချုပ်ကိရိယာများ စီမံပါ',
    'admin.notifications': 'အကြောင်းကြားချက်များ',
    'admin.alerts': 'သတိပေးချက် မှတ်တမ်း',
    'admin.audit': 'Audit Log',
    'admin.settings': 'စနစ်ဆက်တင်',
    'admin.security': 'လုံခြုံရေး',
    'admin.panel': 'အက်ဒမင်ပန်နယ်',
    'admin.nav.system': 'စနစ်',
    'admin.nav.control': 'ထိန်းချုပ်',
    'admin.nav.notify': 'အကြောင်းကြားချက်',
    'admin.nav.settings': 'ဆက်တင်',
    'admin.nav.backToApp': 'အပလီကေးရှင်းသို့ပြန်',
    'admin.nav.automation': 'Automation Rules',
    'admin.nav.scenes': 'Scenes / Presets',
    'admin.nav.controlHistory': 'ထိန်းချုပ်မှတ်တမ်း',

    'admin.user.subtitle': 'အသုံးပြုသူများ ထည့်ရန်၊ ပြင်ဆင်ရန်နှင့် ခွင့်ပြုချက်စီမံရန်',
    'admin.user.searchPlaceholder': 'အသုံးပြုသူရှာရန်...',
    'admin.user.addUser': 'အသုံးပြုသူထည့်ရန်',
    'admin.user.colUser': 'အသုံးပြုသူ',
    'admin.user.colRole': 'ခွင့်ပြုချက်',
    'admin.user.colProject': 'ပရောဂျက်',
    'admin.user.allProjects': 'အားလုံး',
    'admin.user.projectCount': '{n} ပရောဂျက်',
    'admin.user.active': 'အသုံးပြုနေသည်',
    'admin.user.inactive': 'ပိတ်ထားသည်',
    'admin.user.roleAdmin': 'အက်ဒမင်',
    'admin.user.roleOperator': 'အော်ပရေတာ',
    'admin.user.roleViewer': 'ကြည့်ရှုသူ',
    'admin.user.noUser': 'အသုံးပြုသူမတွေ့ပါ',
    'admin.user.createTitle': 'အသုံးပြုသူအသစ်ထည့်ရန်',
    'admin.user.editTitle': 'အသုံးပြုသူပြင်ဆင်ရန်',
    'admin.user.resetTitle': 'စကားဝှက်ပြန်လည်သတ်မှတ်ရန်',
    'admin.user.fieldUsername': 'အသုံးပြုသူအမည်',
    'admin.user.fieldUsernameHint': 'ဝင်ရောက်ရန်အသုံးပြုသည် (အနည်းဆုံး ၃ လုံး)',
    'admin.user.fieldUsernamePlaceholder': 'ဥပမာ somchai',
    'admin.user.fieldEmail': 'အီးမေးလ် (မဖြစ်မနေမဟုတ်)',
    'admin.user.fieldEmailHint': 'ဆက်သွယ်ရန်/စကားဝှက်ပြန်ယူရန် (ချန်ထားနိုင်)',
    'admin.user.fieldEmailPlaceholder': 'name@example.com',
    'admin.user.fieldPassword': 'စကားဝှက်',
    'admin.user.fieldPasswordHint': 'အကြံပြုချက်: လုံခြုံရေးအတွက် အက္ခရာနှင့် ဂဏန်းရောစပ်ပါ',
    'admin.user.fieldPasswordPlaceholder': 'အနည်းဆုံး ၆ လုံး',
    'admin.user.fieldRole': 'ခွင့်ပြုချက်',
    'admin.user.roleHint': 'Viewer = ကြည့်ရုံ, Operator = ထိန်းချုပ်နိုင်, Admin = စီမံနိုင်',
    'admin.user.createSuccess': 'အသုံးပြုသူဖန်တီးပြီးပါပြီ',
    'admin.user.deleteConfirm': '"{name}" ကိုဖျက်မည်လား?',
    'admin.user.deleteSuccess': 'အသုံးပြုသူဖျက်ပြီးပါပြီ',
    'admin.user.noDeleteSuper': 'Super Admin ကိုဖျက်ခွင့်မရှိပါ',
    'admin.user.noToggleSuper': 'Super Admin ကိုဖွင့်/ပိတ်ခွင့်မရှိပါ',
    'admin.user.noEditSuper': 'Admin မှ Super Admin ကိုပြင်ဆင်ခွင့်မရှိပါ',
    'admin.user.superLockRole': 'Super Admin ဖြစ်သည် (Role ပြောင်းမရ) — အမည်နှင့်အီးမေးလ်သာ ပြင်နိုင်သည်',
    'admin.user.activatedMsg': 'အသုံးပြုသူ ဖွင့်ပြီးပါပြီ',
    'admin.user.deactivatedMsg': 'အသုံးပြုသူ ပိတ်ပြီးပါပြီ',
    'admin.user.noChange': 'ပြောင်းလဲမှုမရှိပါ',
    'admin.user.saveSuccess': 'သိမ်းဆည်းပြီးပါပြီ',
    'admin.user.accessTitle': 'ပရောဂျက်ခွင့်ပြုချက်',
    'admin.user.accessDesc': 'ဤအသုံးပြုသူဝင်ရောက်နိုင်သောပရောဂျက်ရွေးပါ',
    'admin.user.accessAll': 'role = {role} သည် "အားလုံး" ဝင်ရောက်နိုင်သည်',
    'admin.user.accessLoading': 'ခွင့်ပြုချက်ဖွင့်နေသည်...',
    'admin.user.accessSaveSuccess': 'ခွင့်ပြုချက်သိမ်းဆည်းပြီးပါပြီ',
    'admin.user.accessLoadError': 'ပရောဂျက်ခွင့်ပြုချက်မဖွင့်နိုင်ပါ',
    'admin.user.superAllAccess': 'Super Admin အားလုံးဝင်ရောက်နိုင်သည်',
    'admin.user.adminAllAccess': 'Admin အားလုံးဝင်ရောက်နိုင်ပြီဖြစ်သည်',
    'admin.user.manageAccess': 'ပရောဂျက်ခွင့်ပြုချက်စီမံရန်',
    'admin.user.onlySuperReset': 'Super Admin သာ စကားဝှက်ပြန်လည်သတ်မှတ်နိုင်သည်',
    'admin.user.resetPassword': 'စကားဝှက်ပြန်လည်သတ်မှတ်ရန်',
    'admin.user.resetFor': 'အသုံးပြုသူ',
    'admin.user.newPassword': 'စကားဝှက်အသစ်',
    'admin.user.newPasswordPlaceholder': 'အနည်းဆုံး ၆ လုံး',
    'admin.user.newPasswordHint': 'အကြံပြုချက်: အက္ခရာ + ဂဏန်းသုံးပါ',
    'admin.user.confirmPassword': 'စကားဝှက်အသစ် အတည်ပြု',
    'admin.user.confirmPasswordPlaceholder': 'ထပ်မံရိုက်ထည့်ပါ',
    'admin.user.showPassword': 'စကားဝှက်ပြရန်',
    'admin.user.hidePassword': 'စကားဝှက်ဝှက်ရန်',
    'admin.user.resetSuccess': '{name} ၏ စကားဝှက်ပြောင်းပြီးပါပြီ',
    'admin.user.resetBtn': 'ပြန်လည်သတ်မှတ်ရန်',

    'admin.project.subtitle': 'ပရောဂျက်များ ထည့်ရန်၊ ပြင်ဆင်ရန်နှင့် သတ်မှတ်ရန်',
    'admin.project.searchPlaceholder': 'ပရောဂျက်ရှာရန်...',
    'admin.project.addProject': 'ပရောဂျက်ထည့်ရန်',
    'admin.project.greenhouse': 'ဖန်လုံအိမ်',
    'admin.project.deleteConfirm': 'ပရောဂျက် "{name}" ကိုဖျက်မည်လား?',
    'admin.project.deleteSuccess': 'ပရောဂျက်ဖျက်ပြီးပါပြီ',
    'admin.project.createSuccess': 'ပရောဂျက်ဖန်တီးပြီးပါပြီ',
    'admin.project.saveSuccess': 'ပရောဂျက်သိမ်းဆည်းပြီးပါပြီ',
    'admin.project.createTitle': 'ပရောဂျက်အသစ်ထည့်ရန်',
    'admin.project.editTitle': 'ပရောဂျက်ပြင်ဆင်ရန်',
    'admin.project.fieldKey': 'Key',
    'admin.project.fieldName': 'ပရောဂျက်အမည်',
    'admin.project.fieldStatus': 'အခြေအနေ',
    'admin.project.statusDeveloping': 'တည်ဆောက်နေဆဲ',
    'admin.project.statusReady': 'အသုံးပြုရန်အဆင်သင့်',
    'admin.project.tbSection': 'ThingsBoard',
    'admin.project.tbUrl': 'URL',
    'admin.project.tbUrlPlaceholder': 'http://thingsboard:8080',
    'admin.project.tbUsername': 'Username',
    'admin.project.tbPassword': 'Password',
    'admin.project.tbPasswordPlaceholder': '(မပြောင်းလဲပါ)',
    'admin.project.noProject': 'ပရောဂျက်မတွေ့ပါ',
    'admin.project.createBtn': 'ဖန်တီးရန်',

    'admin.gh.subtitle': 'ဖန်လုံအိမ်များ ထည့်ရန်၊ ပြင်ဆင်ရန်နှင့် ThingsBoard ချိတ်ဆက်ရန်',
    'admin.gh.searchPlaceholder': 'ဖန်လုံအိမ်ရှာရန်...',
    'admin.gh.allProjects': 'ပရောဂျက်အားလုံး',
    'admin.gh.addGreenhouse': 'ဖန်လုံအိမ်ထည့်ရန်',
    'admin.gh.colGreenhouse': 'ဖန်လုံအိမ်',
    'admin.gh.colProject': 'ပရောဂျက်',
    'admin.gh.colDeviceId': 'Device ID',
    'admin.gh.notConnected': 'မချိတ်ဆက်ရသေးပါ',
    'admin.gh.online': 'အွန်လိုင်း',
    'admin.gh.offline': 'အော့ဖ်လိုင်း',
    'admin.gh.statusReady': 'အဆင်သင့်',
    'admin.gh.statusDeveloping': 'တည်ဆောက်နေဆဲ',
    'admin.gh.noGreenhouse': 'ဖန်လုံအိမ်မတွေ့ပါ',
    'admin.gh.deleteConfirm': 'ဖန်လုံအိမ် "{name}" ကိုဖျက်မည်လား?',
    'admin.gh.deleteSuccess': 'ဖန်လုံအိမ်ဖျက်ပြီးပါပြီ',
    'admin.gh.unlinkConfirm': 'ဤစက်ပစ္စည်းချိတ်ဆက်မှုကို ဖယ်ရှားမည်လား?',
    'admin.gh.unlinkSuccess': 'ချိတ်ဆက်မှုဖယ်ရှားပြီးပါပြီ',
    'admin.gh.unlinkTitle': 'ချိတ်ဆက်မှုဖယ်ရှားရန်',
    'admin.gh.linkTitle': 'စက်ပစ္စည်းချိတ်ဆက်ရန်',
    'admin.gh.createTitle': 'ဖန်လုံအိမ်အသစ်ထည့်ရန်',
    'admin.gh.createSuccess': 'ဖန်လုံအိမ်ဖန်တီးပြီးပါပြီ',
    'admin.gh.fieldProject': 'ပရောဂျက်',
    'admin.gh.fieldKey': 'Key (ဥပမာ greenhouse1)',
    'admin.gh.fieldName': 'ဖန်လုံအိမ်အမည်',
    'admin.gh.fieldDeviceId': 'ThingsBoard Device ID (ရှိလျှင်)',
    'admin.gh.fieldDeviceIdOptional': 'မဖြစ်မနေမဟုတ်',
    'admin.gh.createBtn': 'ဖန်တီးရန်',
    'admin.gh.linkDeviceTitle': 'စက်ပစ္စည်းချိတ်ဆက်ရန်',
    'admin.gh.linkDeviceHint': 'ThingsBoard မှ Device ID ကူးယူပါ (Devices → Device Details → Copy device id)',
    'admin.gh.linkSuccess': 'စက်ပစ္စည်းချိတ်ဆက်ပြီးပါပြီ',
    'admin.gh.linkBtn': 'ချိတ်ဆက်ရန်',

    'admin.sensor.subtitle': 'Sensor များ ထည့်ရန်နှင့် ပြင်ဆင်ရန်',
    'admin.sensor.selectProject': 'ပရောဂျက်ရွေးပါ...',
    'admin.sensor.selectProjectHint': 'စီမံရန် ပရောဂျက်ရွေးပါ',
    'admin.sensor.selectGh': 'ဖန်လုံအိမ်ရွေးပါ...',
    'admin.sensor.selectGhHint': 'Sensor သတ်မှတ်ရန် ဖန်လုံအိမ်ရွေးပါ',
    'admin.sensor.pleaseSelect': 'ပရောဂျက်နှင့် ဖန်လုံအိမ်ရွေးပါ',
    'admin.sensor.noSensor': 'Sensor မရှိပါ — Template နှိပ်၍ ဖန်တီးပါ',
    'admin.sensor.colType': 'အမျိုးအစား',
    'admin.sensor.colUnit': 'ယူနစ်',
    'admin.sensor.colAlert': 'သတိပေးချက်',
    'admin.sensor.deleteConfirm': '"{name}" ကိုဖျက်မည်လား?',
    'admin.sensor.deleteSuccess': 'ဖျက်ပြီးပါပြီ',
    'admin.sensor.saveSuccess': 'သိမ်းဆည်းပြီးပါပြီ',
    'admin.sensor.createTitle': 'Sensor ထည့်ရန်',
    'admin.sensor.editTitle': 'Sensor ပြင်ဆင်ရန်',
    'admin.sensor.fieldKey': 'Sensor Key (အင်္ဂလိပ်)',
    'admin.sensor.fieldKeyHint': 'ထူးခြားသော key ဥပမာ air_temp — အက္ခရာသေး၊ underscore နှင့် ဂဏန်းသာ',
    'admin.sensor.fieldName': 'Sensor အမည်',
    'admin.sensor.fieldNameHint': 'ပြသမည့်အမည် ဥပမာ လေအပူချိန်',
    'admin.sensor.fieldType': 'အမျိုးအစား',
    'admin.sensor.fieldTypeHint': 'ပြသမှုအုပ်စု',
    'admin.sensor.fieldUnit': 'ယူနစ်',
    'admin.sensor.fieldUnitHint': 'တိုင်းတာသည့်ယူနစ်',
    'admin.sensor.fieldUnitPlaceholder': '°C, %, ppm',
    'admin.sensor.fieldDataKey': 'Data Key (ThingsBoard)',
    'admin.sensor.fieldDataKeyHint': 'ThingsBoard မှ Telemetry Key — Device ပေးပို့သည့်အမည်နှင့်တူရမည်',
    'admin.sensor.alertMin': 'သတိပေး (နည်းလွန်)',
    'admin.sensor.alertMinHint': 'ချန်ထားလျှင် = သတိမပေး',
    'admin.sensor.alertMax': 'သတိပေး (များလွန်)',
    'admin.sensor.alertMaxHint': 'ချန်ထားလျှင် = သတိမပေး',
    'admin.sensor.minValue': 'gauge အနည်းဆုံး',
    'admin.sensor.minValueHint': 'ချန်ထားလျှင် = dashboard ပုံသေတန်ဖိုး',
    'admin.sensor.maxValue': 'gauge အများဆုံး',
    'admin.sensor.maxValueHint': 'ချန်ထားလျှင် = dashboard ပုံသေတန်ဖိုး',
    'admin.sensor.calibration': 'Calibration',
    'admin.sensor.calibOffset': 'Offset (+/-)',
    'admin.sensor.calibOffsetHint': 'တန်ဖိုးအပေါင်း/အနှုတ်',
    'admin.sensor.calibScale': 'Scale (x)',
    'admin.sensor.calibScaleHint': 'မြှောက်ဖော် (1 = မပြောင်းလဲ)',
    'admin.sensor.templateTitle': 'Template ရွေးပါ',
    'admin.sensor.templateDesc': 'Sensor အစုံ အလိုအလျောက်ဖန်တီးမည် (ရှိပြီးသားများ ကျော်သွားမည်)',
    'admin.sensor.templateSuccess': 'Sensor များဖန်တီးပြီးပါပြီ',
    'admin.sensor.typeAir': 'လေ',
    'admin.sensor.typeSoil': 'မြေ',
    'admin.sensor.typeWater': 'ရေ',
    'admin.sensor.typeLight': 'အလင်း',
    'admin.sensor.typeCustom': 'အခြား',

    'admin.notif.subtitle': 'Line Notify နှင့် သတိပေးချက်သတ်မှတ်ရန်',
    'admin.notif.lineNotify': 'Line Notify',
    'admin.notif.lineNotifyDesc': 'အရေးကြီးသောဖြစ်ရပ်များတွင် Line မှ သတိပေးချက်ရယူပါ',
    'admin.notif.tokenSection': 'ချိတ်ဆက်မှု Token',
    'admin.notif.currentToken': 'လက်ရှိ Token:',
    'admin.notif.newToken': 'Line Notify Token အသစ်',
    'admin.notif.tokenUnchanged': '(မပြောင်းလဲပါ)',
    'admin.notif.tokenPlaceholder': 'Token ကူးထည့်ပါ',
    'admin.notif.tokenHowTo': 'Token ရယူနည်း:',
    'admin.notif.tokenStep1': '1. notify-bot.line.me သို့သွားပါ',
    'admin.notif.tokenStep2': '2. Line Account ဖြင့် Login ဝင်ပါ',
    'admin.notif.tokenStep3': '3. "Generate Token" နှိပ်ပါ',
    'admin.notif.tokenStep4': '4. သတိပေးချက်လက်ခံမည့် Group သို့မဟုတ် 1-on-1 ရွေးပါ',
    'admin.notif.tokenStep5': '5. Token ကူးယူ၍ ဤနေရာတွင် ထည့်ပါ',
    'admin.notif.alertTypes': 'သတိပေးချက်အမျိုးအစား',
    'admin.notif.alertOffline': 'စက်ပစ္စည်း offline ဖြစ်သည့်အခါ သတိပေးရန်',
    'admin.notif.alertOfflineDesc': 'စက်ပစ္စည်းချိတ်ဆက်မှုပြတ်သွားသည့်အခါ မက်ဆေ့ပို့ရန်',
    'admin.notif.alertThreshold': 'တန်ဖိုးကျော်လွန်သည့်အခါ သတိပေးရန်',
    'admin.notif.alertThresholdDesc': 'Sensor တန်ဖိုးမမှန်သည့်အခါ မက်ဆေ့ပို့ရန်',
    'admin.notif.thresholds': 'သတိပေးချက်အဆင့်',
    'admin.notif.tempLabel': 'လေအပူချိန် (°C)',
    'admin.notif.humidityLabel': 'လေစိုထိုင်းဆ (%)',
    'admin.notif.soilLabel': 'မြေစိုထိုင်းဆ (%)',
    'admin.notif.min': 'အနည်းဆုံး',
    'admin.notif.max': 'အများဆုံး',
    'admin.notif.testBtn': 'စမ်းသပ်မက်ဆေ့ပို့ရန်',
    'admin.notif.saveBtn': 'ဆက်တင်သိမ်းဆည်းရန်',
    'admin.notif.saveSuccess': 'ဆက်တင်သိမ်းဆည်းပြီးပါပြီ',
    'admin.notif.testSuccess': 'စမ်းသပ်မက်ဆေ့ပို့ပြီးပါပြီ',

    'admin.alert.subtitle': 'သတိပေးချက်အားလုံးကြည့်ရန်',
    'admin.alert.statTotal': 'စုစုပေါင်း',
    'admin.alert.statPending': 'စောင့်ဆိုင်းနေသည်',
    'admin.alert.statCritical': 'အရေးပေါ်',
    'admin.alert.statAcked': 'လက်ခံပြီး',
    'admin.alert.allProjects': 'ပရောဂျက်အားလုံး',
    'admin.alert.allLevels': 'အဆင့်အားလုံး',
    'admin.alert.allStatus': 'အားလုံး',
    'admin.alert.pending': 'မလက်ခံရသေးပါ',
    'admin.alert.acked': 'လက်ခံပြီး',
    'admin.alert.ackedBy': 'မှ',
    'admin.alert.waitAck': 'စောင့်ဆိုင်းနေသည်',
    'admin.alert.ackBtn': 'လက်ခံရန်',
    'admin.alert.ackSuccess': 'လက်ခံပြီးပါပြီ',
    'admin.alert.ackAllConfirm': 'မလက်ခံရသေးသော သတိပေးချက်အားလုံး လက်ခံမည်လား?',
    'admin.alert.ackAllBtn': 'အားလုံးလက်ခံရန်',
    'admin.alert.ackAllSuccess': 'အားလုံးလက်ခံပြီးပါပြီ',
    'admin.alert.severityCritical': 'အရေးပေါ်',
    'admin.alert.severityWarning': 'သတိပေး',
    'admin.alert.severityInfo': 'သတင်းအချက်အလက်',
    'admin.alert.colTime': 'အချိန်',
    'admin.alert.colLevel': 'အဆင့်',
    'admin.alert.colGreenhouse': 'ဖန်လုံအိမ်',
    'admin.alert.colMessage': 'မက်ဆေ့',
    'admin.alert.noAlert': 'သတိပေးချက်မရှိပါ',
    'admin.alert.dirAbove': 'ကျော်လွန်',
    'admin.alert.dirBelow': 'နည်းသည်',
    'admin.alert.showCount': '{shown} / {total} ပြသနေသည်',

    'admin.audit.subtitle': 'စနစ်အသုံးပြုမှုမှတ်တမ်းအားလုံး',
    'admin.audit.statLogs': 'မှတ်တမ်း (၇ ရက်)',
    'admin.audit.statUsers': 'အသုံးပြုသူများ',
    'admin.audit.topActions': 'အဓိက Action များ',
    'admin.audit.allActions': 'Action အားလုံး',
    'admin.audit.colTime': 'အချိန်',
    'admin.audit.colUser': 'အသုံးပြုသူ',
    'admin.audit.colAction': 'Action',
    'admin.audit.colProjectGh': 'Project/GH',
    'admin.audit.colDetail': 'အသေးစိတ်',
    'admin.audit.noLog': 'မှတ်တမ်းမရှိပါ',
    'admin.audit.system': 'စနစ်',
    'admin.audit.showCount': '{shown} / {total} ပြသနေသည်',
    'admin.audit.exportCsv': 'CSV ထုတ်ယူရန်',

    'admin.settings.subtitle': 'စနစ်အချက်အလက်နှင့် ယေဘုယျဆက်တင်များကြည့်ရန်',
    'admin.settings.systemInfo': 'စနစ်အချက်အလက်',
    'admin.settings.version': 'ဗားရှင်း',
    'admin.settings.nodejs': 'Node.js',
    'admin.settings.database': 'ဒေတာဘေ့စ်',
    'admin.settings.dbMain': 'PostgreSQL Database',
    'admin.settings.dbMainDesc': 'အဓိကဒေတာဘေ့စ်ဖိုင်',
    'admin.settings.connected': 'ချိတ်ဆက်ပြီး',
    'admin.settings.backup': 'အရန်သိမ်းဆည်းမှု',
    'admin.settings.backupEnabled': 'ဖွင့်ထားသည်',
    'admin.settings.backupDisabled': 'ပိတ်ထားသည်',
    'admin.settings.backupEvery': 'တိုင်း {n} ရက်',
    'admin.settings.backupBtn': 'အရန်သိမ်းဆည်းရန်',
    'admin.settings.security': 'လုံခြုံရေး',
    'admin.settings.sessionTimeout': 'Session Timeout',
    'admin.settings.sessionTimeoutValue': '24 နာရီ',
    'admin.settings.auditLog': 'Audit Log',
    'admin.settings.auditLogDesc': 'စနစ်သည် login, ထိန်းချုပ်မှု, ဒေတာပြင်ဆင်မှုများအပါအဝင် အသုံးပြုသူလုပ်ဆောင်ချက်အားလုံး မှတ်တမ်းတင်သည်',
    'admin.settings.auditLogTip': 'Audit Log မီနူးမှ ကြည့်ရှုနိုင်သည် သို့မဟုတ် CSV အဖြစ် ထုတ်ယူနိုင်သည်',
    'admin.settings.refresh': 'ပြန်လည်ဖွင့်ရန်',
    'admin.settings.loadError': 'ဒေတာမဖွင့်နိုင်ပါ',

    'admin.security.subtitle': 'စနစ်လုံခြုံရေးစီမံခန့်ခွဲမှု',
    'admin.security.tabHistory': 'Login မှတ်တမ်း',
    'admin.security.tabSessions': 'Sessions',
    'admin.security.tabIp': 'IP Whitelist',
    'admin.security.statTotal': 'Login စုစုပေါင်း',
    'admin.security.statSuccess': 'အောင်မြင်သည်',
    'admin.security.statFailed': 'မအောင်မြင်ပါ',
    'admin.security.statBlocked': 'ပိတ်ဆို့ခံရသည်',
    'admin.security.statUsers': 'အသုံးပြုသူများ',
    'admin.security.colTime': 'အချိန်',
    'admin.security.colUser': 'အသုံးပြုသူ',
    'admin.security.colIp': 'IP',
    'admin.security.colStatus': 'အခြေအနေ',
    'admin.security.colLastActive': 'နောက်ဆုံးအသုံးပြုချိန်',
    'admin.security.colExpires': 'သက်တမ်းကုန်ချိန်',
    'admin.security.colDescription': 'ဖော်ပြချက်',
    'admin.security.colAddedBy': 'ထည့်သွင်းသူ',
    'admin.security.colDate': 'နေ့စွဲ',
    'admin.security.statusSuccess': 'အောင်မြင်သည်',
    'admin.security.statusFailed': 'မအောင်မြင်ပါ',
    'admin.security.statusBlocked': 'ပိတ်ဆို့ခံရသည်',
    'admin.security.terminateConfirm': 'ဤ Session ကိုဖျက်မည်လား?',
    'admin.security.terminateSuccess': 'Session ဖျက်ပြီးပါပြီ',
    'admin.security.terminateBtn': 'ဖျက်ရန်',
    'admin.security.noSession': 'Session မရှိပါ',
    'admin.security.ipWhitelist': 'IP Whitelist',
    'admin.security.ipWhitelistDesc': 'သတ်မှတ်ထားသော IP များသာ ခွင့်ပြုရန်',
    'admin.security.ipEnabled': 'ဖွင့်',
    'admin.security.ipDisabled': 'ပိတ်',
    'admin.security.ipWarning': '⚠️ သတိပေးချက်: ဖွင့်ပြီး IP မထည့်မိပါက Login ဝင်မရနိုင်ပါ!',
    'admin.security.addIp': 'IP ထည့်ရန်',
    'admin.security.ipPlaceholder': 'IP Address (ဥပမာ 192.168.1.1)',
    'admin.security.ipDescPlaceholder': 'ဖော်ပြချက်',
    'admin.security.addIpSuccess': 'IP ထည့်ပြီးပါပြီ',
    'admin.security.removeIpConfirm': 'ဤ IP ကိုဖျက်မည်လား?',
    'admin.security.removeIpSuccess': 'IP ဖျက်ပြီးပါပြီ',
    'admin.security.noIp': 'IP မရှိသေးပါ',
    'admin.security.toggleOnSuccess': 'IP Whitelist ဖွင့်ပြီးပါပြီ',
    'admin.security.toggleOffSuccess': 'IP Whitelist ပိတ်ပြီးပါပြီ',
    'admin.security.error': 'အမှားဖြစ်သည်',

    'admin.auto.subtitle': 'အလိုအလျောက်စည်းမျဉ်းများ — X ဆိုရင် Y လုပ်ရန်',
    'admin.auto.selectProject': 'ပရောဂျက်',
    'admin.auto.selectProjectPlaceholder': 'ပရောဂျက်ရွေးပါ...',
    'admin.auto.selectGh': 'ဖန်လုံအိမ်',
    'admin.auto.selectGhPlaceholder': 'ဖန်လုံအိမ်ရွေးပါ...',
    'admin.auto.addRule': 'စည်းမျဉ်းထည့်ရန်',
    'admin.auto.pleaseSelect': 'ပရောဂျက်နှင့် ဖန်လုံအိမ်ရွေးပါ',
    'admin.auto.noRule': 'အလိုအလျောက်စည်းမျဉ်းမရှိသေးပါ',
    'admin.auto.lastTriggered': 'နောက်ဆုံးအလုပ်လုပ်သည်:',
    'admin.auto.triggerCount': '{n} ကြိမ်',
    'admin.auto.toggleSuccess': 'စည်းမျဉ်းဖွင့်ပြီးပါပြီ',
    'admin.auto.toggleOffSuccess': 'စည်းမျဉ်းပိတ်ပြီးပါပြီ',
    'admin.auto.deleteConfirm': 'စည်းမျဉ်း "{name}" ကိုဖျက်မည်လား?',
    'admin.auto.deleteSuccess': 'ဖျက်ပြီးပါပြီ',
    'admin.auto.error': 'အမှားဖြစ်သည်',
    'admin.auto.loadError': 'ဒေတာမဖွင့်နိုင်ပါ',
    'admin.auto.triggerSensor': 'Sensor တန်ဖိုးအရ',
    'admin.auto.triggerTime': 'အချိန်အတိုင်း',
    'admin.auto.triggerManual': 'လက်ဖြင့်',
    'admin.auto.condAbove': 'ကျော်လွန်သည်',
    'admin.auto.condBelow': 'နည်းသည်',
    'admin.auto.condEqual': 'တူညီသည်',
    'admin.auto.condBetween': 'အကြားတွင်',
    'admin.auto.timeAt': 'အချိန်',
    'admin.auto.actionsCount': '{n} လုပ်ဆောင်ချက်',
    'admin.auto.createTitle': 'စည်းမျဉ်းအသစ်ဖန်တီးရန်',
    'admin.auto.editTitle': 'စည်းမျဉ်းပြင်ဆင်ရန်',
    'admin.auto.fieldName': 'စည်းမျဉ်းအမည်',
    'admin.auto.fieldNamePlaceholder': 'ဥပမာ ပူလွန်းသည့်အခါ ပန်ကာဖွင့်ရန်',
    'admin.auto.fieldDesc': 'ဖော်ပြချက် (မဖြစ်မနေမဟုတ်)',
    'admin.auto.fieldDescPlaceholder': 'အသေးစိတ်ထပ်ဖြည့်ပါ',
    'admin.auto.triggerType': 'Trigger အမျိုးအစား',
    'admin.auto.sensorKey': 'Sensor Key',
    'admin.auto.sensorKeyPlaceholder': 'ဥပမာ air_temp',
    'admin.auto.condition': 'အခြေအနေ',
    'admin.auto.value': 'တန်ဖိုး',
    'admin.auto.time': 'အချိန်',
    'admin.auto.actions': 'လုပ်ဆောင်ချက်များ',
    'admin.auto.addAction': 'လုပ်ဆောင်ချက်ထည့်ရန်',
    'admin.auto.actionControl': 'စက်ပစ္စည်းထိန်းချုပ်ရန်',
    'admin.auto.actionNotif': 'အကြောင်းကြားချက်ပို့ရန်',
    'admin.auto.controlKeyPlaceholder': 'Control Key (ဥပမာ fan_1)',
    'admin.auto.actionOn': 'ဖွင့်',
    'admin.auto.actionOff': 'ပိတ်',
    'admin.auto.actionToggle': 'ပြောင်းလဲ',
    'admin.auto.messagePlaceholder': 'မက်ဆေ့',
    'admin.auto.saveSuccess': 'သိမ်းဆည်းပြီးပါပြီ',

    'admin.scene.subtitle': 'အသင့်သုံးအမိန့်စုများ ဥပမာ မနက်၊ နေ့ခင်း၊ ညနေ',
    'admin.scene.selectProject': 'ပရောဂျက်',
    'admin.scene.selectProjectPlaceholder': 'ပရောဂျက်ရွေးပါ...',
    'admin.scene.selectGh': 'ဖန်လုံအိမ်',
    'admin.scene.selectGhPlaceholder': 'ဖန်လုံအိမ်ရွေးပါ...',
    'admin.scene.addScene': 'Scene ဖန်တီးရန်',
    'admin.scene.pleaseSelect': 'ပရောဂျက်နှင့် ဖန်လုံအိမ်ရွေးပါ',
    'admin.scene.noScene': 'Scene မရှိသေးပါ',
    'admin.scene.executeSuccess': '"{name}" အောင်မြင်စွာ လုပ်ဆောင်ပြီးပါပြီ',
    'admin.scene.deleteConfirm': 'Scene "{name}" ကိုဖျက်မည်လား?',
    'admin.scene.deleteSuccess': 'ဖျက်ပြီးပါပြီ',
    'admin.scene.error': 'အမှားဖြစ်သည်',
    'admin.scene.loadError': 'ဒေတာမဖွင့်နိုင်ပါ',
    'admin.scene.executeBtn': 'လုပ်ဆောင်ရန်',
    'admin.scene.iconLabel': 'Icon',
    'admin.scene.iconLightning': 'လျှပ်စီး',
    'admin.scene.iconDay': 'နေ့ခင်း',
    'admin.scene.iconNight': 'ညဘက်',
    'admin.scene.iconMorning': 'မနက်',
    'admin.scene.colorLabel': 'အရောင်',
    'admin.scene.createTitle': 'Scene အသစ်ဖန်တီးရန်',
    'admin.scene.editTitle': 'Scene ပြင်ဆင်ရန်',
    'admin.scene.fieldName': 'Scene အမည်',
    'admin.scene.fieldNamePlaceholder': 'ဥပမာ မနက်၊ နေ့ခင်း၊ ညနေ',
    'admin.scene.fieldDesc': 'ဖော်ပြချက်',
    'admin.scene.actions': 'လုပ်ဆောင်ချက်များ',
    'admin.scene.actionOn': 'ဖွင့်',
    'admin.scene.actionOff': 'ပိတ်',
    'admin.scene.saveSuccess': 'သိမ်းဆည်းပြီးပါပြီ',

    'admin.ctrl.subtitle': 'စက်ပစ္စည်းဖွင့်/ပိတ်မှတ်တမ်းအားလုံး',
    'admin.ctrl.selectProject': 'ပရောဂျက်',
    'admin.ctrl.selectProjectPlaceholder': 'ပရောဂျက်ရွေးပါ...',
    'admin.ctrl.selectGh': 'ဖန်လုံအိမ်',
    'admin.ctrl.selectGhPlaceholder': 'ဖန်လုံအိမ်ရွေးပါ...',
    'admin.ctrl.loadBtn': 'မှတ်တမ်းဖွင့်ရန်',
    'admin.ctrl.pleaseSelect': 'ပရောဂျက်နှင့် ဖန်လုံအိမ်ရွေးပါ',
    'admin.ctrl.statTotal': 'စုစုပေါင်း',
    'admin.ctrl.statManual': 'လက်ဖြင့်',
    'admin.ctrl.statSchedule': 'အချိန်ဇယား',
    'admin.ctrl.statAuto': 'အလိုအလျောက်',
    'admin.ctrl.srcManual': 'လက်ဖြင့်',
    'admin.ctrl.srcSchedule': 'အချိန်ဇယား',
    'admin.ctrl.srcAuto': 'အလိုအလျောက်',
    'admin.ctrl.srcScene': 'Scene',
    'admin.ctrl.colTime': 'အချိန်',
    'admin.ctrl.colDevice': 'စက်ပစ္စည်း',
    'admin.ctrl.colAction': 'လုပ်ဆောင်ချက်',
    'admin.ctrl.colSource': 'ရင်းမြစ်',
    'admin.ctrl.colUser': 'အသုံးပြုသူ',
    'admin.ctrl.actionOn': 'ဖွင့်',
    'admin.ctrl.actionOff': 'ပိတ်',
    'admin.ctrl.noHistory': 'မှတ်တမ်းမရှိသေးပါ',
    'admin.ctrl.loadError': 'ဒေတာမဖွင့်နိုင်ပါ',

    'profile.title': 'ပရိုဖိုင်',
    'profile.subtitle': 'ကိုယ်ရေးနှင့် ဆက်တင်များကို စီမံပါ',
    'profile.lastLogin': 'နောက်ဆုံးဝင်ရောက်မှု:',
    'profile.createdAt': 'ဖန်တီးသည့်နေ့:',
    'profile.changePassword': 'စကားဝှက်ပြောင်းမယ်',
    'profile.currentPassword': 'လက်ရှိစကားဝှက်',
    'profile.newPassword': 'စကားဝှက်အသစ်',
    'profile.confirmPassword': 'စကားဝှက်အသစ် အတည်ပြု',
    'profile.passwordMismatch': 'စကားဝှက်မကိုက်ညီပါ',
    'profile.passwordMin6': 'အနည်းဆုံး 6 လုံးလိုသည်',
    'profile.passwordChanged': 'စကားဝှက်ပြောင်းပြီးပါပြီ',
    'profile.preferencesTitle': 'ကိုယ်ပိုင်ဆက်တင်',
    'profile.language': 'ဘာသာစကား',
    'profile.theme': 'အပြင်အဆင်',
    'profile.themeLight': '☀️ အလင်း',
    'profile.themeDark': '🌙 မှောင်',
    'profile.themeSystem': '💻 စနစ်အတိုင်း',
    'profile.savePreferences': 'သိမ်းဆည်းပါ',
    'profile.preferencesSaved': 'သိမ်းဆည်းပြီးပါပြီ',
    'profile.strengthWeak': 'အားနည်း',
    'profile.strengthMedium': 'အလယ်အလတ်',
    'profile.strengthStrong': 'အားကောင်း',

    'agri.title': 'စိုက်ပျိုးရေး',
    'agri.subtitle': 'စီမံရန် အမျိုးအစားရွေးပါ',
    'agri.menu.crops': 'သီးနှံစီမံပါ',
    'agri.menu.cropsDesc': 'စိုက်ပျိုးသောသီးနှံများ မှတ်တမ်းတင်ပါ',
    'agri.menu.growth': 'ကြီးထွားမှုမှတ်တမ်း',
    'agri.menu.growthDesc': 'အပင်ကြီးထွားမှု ခြေရာခံပါ',
    'agri.menu.fertilizer': 'ဓာတ်မြေသြဇာဇယားကွက်',
    'agri.menu.fertilizerDesc': 'ဓာတ်မြေသြဇာ စီစဉ်ပါ',
    'agri.menu.pestDisease': 'ပိုးမွှားနှင့်ရောဂါ',
    'agri.menu.pestDiseaseDesc': 'ပိုးမွှားနှင့်ရောဂါ မှတ်တမ်းတင်ပါ',
    'agri.menu.yield': 'အထွက်နှုန်း',
    'agri.menu.yieldDesc': 'သီးနှံရိတ်သိမ်းမှုနှင့် ဝင်ငွေ ခြေရာခံပါ',
    'agri.menu.water': 'ရေသုံးစွဲမှု',
    'agri.menu.waterDesc': 'ရေပမာဏနှင့် ကုန်ကျစရိတ် ခြေရာခံပါ',

    'agri.crop.status.planted': 'စိုက်ပြီး',
    'agri.crop.status.growing': 'ကြီးထွားနေသည်',
    'agri.crop.status.harvested': 'ရိတ်သိမ်းပြီး',
    'agri.crop.status.failed': 'ကျရှုံးသည်',
    'agri.crop.statTotal': 'စုစုပေါင်းသီးနှံ',
    'agri.crop.statNearHarvest': 'ရိတ်သိမ်းရန်နီး (7ရက်)',
    'agri.crop.addCrop': 'သီးနှံထည့်ရန်',
    'agri.crop.addTitle': 'သီးနှံအသစ်ထည့်ရန်',
    'agri.crop.editTitle': 'သီးနှံပြင်ဆင်ရန်',
    'agri.crop.confirmDelete': 'ဖျက်ရန်',
    'agri.crop.confirmHarvest': 'ရိတ်သိမ်းမှတ်တမ်း',
    'agri.crop.noData': 'သီးနှံဒေတာမရှိသေးပါ',
    'agri.crop.colName': 'သီးနှံ',
    'agri.crop.colPlantDate': 'စိုက်သည့်နေ့',
    'agri.crop.colExpectedHarvest': 'ခန့်မှန်းရိတ်သိမ်းနေ့',
    'agri.crop.variety': 'မျိုးကွဲ',
    'agri.crop.daysLeft': 'နောက် {n} ရက်',
    'agri.crop.fieldName': 'သီးနှံအမည်',
    'agri.crop.fieldNamePlaceholder': 'ဥပမာ ဆလတ်',
    'agri.crop.fieldVariety': 'မျိုးကွဲ',
    'agri.crop.fieldVarietyPlaceholder': 'ဥပမာ Red Oak',
    'agri.col.greenhouse': 'ဖန်လုံအိမ်',
    'agri.col.quantity': 'အရေအတွက်',
    'agri.col.unit': 'ယူနစ်',
    'agri.filter.allProjects': 'ပရောဂျက်အားလုံး',
    'agri.filter.allGreenhouses': 'ဖန်လုံအိမ်အားလုံး',
    'agri.filter.allStatus': 'အခြေအနေအားလုံး',
    'agri.label.project': 'ပရောဂျက်',
    'agri.label.greenhouse': 'ဖန်လုံအိမ်',
    'agri.label.select': 'ရွေးပါ...',
    'agri.label.notes': 'မှတ်တမ်း',
    'agri.label.notesPlaceholder': 'အသေးစိတ်ထပ်ဖြည့်ပါ...',
    'agri.unit.plant': 'ပင်',
    'agri.unit.tray': 'ပန်းကန်',
    'agri.unit.plot': 'ကွက်',
    'agri.unit.kg': 'ကီလို',

    'agri.fertilizer.overdue': 'သတ်မှတ်ချိန်ကျော်',
    'agri.fertilizer.today': 'ယနေ့',
    'agri.fertilizer.upcoming': 'လာမည့်အချိန်',
    'agri.fertilizer.completed': 'ပြီးဆုံးပြီ',
    'agri.fertilizer.showCompleted': 'ပြီးဆုံးသည်များ ပြရန်',
    'agri.fertilizer.addSchedule': 'ဇယားထည့်ရန်',
    'agri.fertilizer.addTitle': 'ဓာတ်မြေသြဇာဇယား ထည့်ရန်',
    'agri.fertilizer.editTitle': 'ဓာတ်မြေသြဇာဇယား ပြင်ဆင်ရန်',
    'agri.fertilizer.addSuccess': 'ဇယားထည့်ပြီးပါပြီ',
    'agri.fertilizer.completeSuccess': 'မှတ်တမ်းတင်ပြီးပါပြီ',
    'agri.fertilizer.completedAt': 'ပြီးသည်',
    'agri.fertilizer.confirmDelete': 'ဖျက်ရန်',
    'agri.fertilizer.noData': 'ဓာတ်မြေသြဇာဇယားမရှိသေးပါ',
    'agri.fertilizer.fieldName': 'ဓာတ်မြေသြဇာအမည်',
    'agri.fertilizer.fieldNamePlaceholder': 'ဥပမာ NPK 16-16-16',
    'agri.fertilizer.fieldType': 'အမျိုးအစား',
    'agri.fertilizer.fieldDate': 'နေ့စွဲ',
    'agri.fertilizer.fieldAmount': 'ပမာဏ',
    'agri.fertilizer.typeChemical': 'ဓာတုဓာတ်မြေသြဇာ',
    'agri.fertilizer.typeOrganic': 'သဘာဝဓာတ်မြေသြဇာ',
    'agri.fertilizer.typeBio': 'ဇီဝဓာတ်မြေသြဇာ',
    'agri.fertilizer.typeFoliar': 'အရွက်ဖြန်းဓာတ်မြေသြဇာ',

    'agri.growth.health.excellent': 'အလွန်ကောင်း',
    'agri.growth.health.good': 'ကောင်း',
    'agri.growth.health.fair': 'သင့်တော်သည်',
    'agri.growth.health.poor': 'မကောင်း',
    'agri.growth.selectCrop': 'သီးနှံရွေးပါ',
    'agri.growth.selectCropPlaceholder': 'ခြေရာခံရန် သီးနှံရွေးပါ...',
    'agri.growth.addRecord': 'မှတ်တမ်းထည့်ရန်',
    'agri.growth.addTitle': 'ကြီးထွားမှုမှတ်တမ်း ထည့်ရန်',
    'agri.growth.editTitle': 'မှတ်တမ်းပြင်ဆင်ရန်',
    'agri.growth.pleaseSelect': 'သီးနှံရွေးပါ',
    'agri.growth.confirmDelete': 'ဤမှတ်တမ်းကို ဖျက်မည်လား?',
    'agri.growth.noData': 'မှတ်တမ်းမရှိသေးပါ',
    'agri.growth.totalGrowth': 'စုစုပေါင်းကြီးထွားမှု',
    'agri.growth.avgRate': 'ပျမ်းမျှကြီးထွားနှုန်း',
    'agri.growth.recordCount': 'မှတ်တမ်းအရေအတွက်',
    'agri.growth.day': 'ရက်',
    'agri.growth.colDate': 'နေ့စွဲ',
    'agri.growth.colHeight': 'အမြင့်',
    'agri.growth.colLeafCount': 'အရွက်အရေအတွက်',
    'agri.growth.colHealth': 'ကျန်းမာရေး',
    'agri.growth.colPhoto': 'ဓာတ်ပုံ',
    'agri.growth.colRecordedBy': 'မှတ်တမ်းတင်သူ',
    'agri.growth.viewPhoto': 'ဓာတ်ပုံကြည့်ရန်',
    'agri.growth.fieldHeight': 'အမြင့် (cm)',
    'agri.growth.fieldLeafCount': 'အရွက်အရေအတွက်',
    'agri.growth.fieldPhotoUrl': 'ဓာတ်ပုံ URL',

    'agri.pest.title': 'ပိုးမွှားနှင့်ရောဂါ',
    'agri.pest.subtitle': 'ပိုးမွှားနှင့်ရောဂါ မှတ်တမ်းတင်ပါ',
    'agri.pest.typePest': 'ပိုးမွှား',
    'agri.pest.typeDisease': 'အပင်ရောဂါ',
    'agri.pest.typePestShort': 'ပိုး',
    'agri.pest.typeDiseaseShort': 'ရောဂါ',
    'agri.pest.severity.low': 'နည်း',
    'agri.pest.severity.medium': 'အလတ်',
    'agri.pest.severity.high': 'များ',
    'agri.pest.severity.critical': 'ဆိုးရွားသည်',
    'agri.pest.statCritical': 'ပြင်းထန်သည်',
    'agri.pest.statResolved': 'ဖြေရှင်းပြီး',
    'agri.pest.filterAll': 'အမျိုးအစားအားလုံး',
    'agri.pest.showResolved': 'ဖြေရှင်းပြီးသည်များ ပြရန်',
    'agri.pest.addRecord': 'ပြဿနာမှတ်တမ်းတင်ရန်',
    'agri.pest.addTitle': 'ပိုးမွှား/ရောဂါ မှတ်တမ်းတင်ရန်',
    'agri.pest.editTitle': 'မှတ်တမ်းပြင်ဆင်ရန်',
    'agri.pest.confirmDelete': 'ဤမှတ်တမ်းကို ဖျက်မည်လား?',
    'agri.pest.resolved': 'ဖြေရှင်းပြီး',
    'agri.pest.markResolved': 'ဖြေရှင်းပြီးဟု မှတ်ရန်',
    'agri.pest.resolveSuccess': 'ဖြေရှင်းပြီးဟု မှတ်တမ်းတင်ပြီး',
    'agri.pest.fieldType': 'အမျိုးအစား',
    'agri.pest.fieldName': 'ပိုးမွှား/ရောဂါအမည်',
    'agri.pest.fieldNamePlaceholder': 'ဥပမာ aphids, powdery mildew',
    'agri.pest.fieldSeverity': 'ပြင်းထန်မှု',
    'agri.pest.fieldAffectedArea': 'ဖြစ်ပွားသောနေရာ',
    'agri.pest.fieldAffectedAreaPlaceholder': 'ဥပမာ ကွက် 1-3',
    'agri.pest.fieldTreatment': 'ကုသမှု/ဖယ်ရှားမှု',
    'agri.pest.fieldTreatmentPlaceholder': 'ဥပမာ ဇီဝပိုးသတ်ဆေး ဖြန်းရန်',

    'agri.water.statTotalM3': 'စုစုပေါင်း (m³)',
    'agri.water.statAvgPerDay': 'ပျမ်းမျှ/ရက် (L)',
    'agri.water.statTotalCost': 'စုစုပေါင်းကုန်ကျစရိတ်',
    'agri.water.statPerM3': 'တစ် m³ လျှင်',
    'agri.water.chartTitle': 'ရေသုံးစွဲမှု',
    'agri.water.addRecord': 'ရေသုံးစွဲမှု မှတ်တမ်းတင်ရန်',
    'agri.water.addTitle': 'ရေသုံးစွဲမှု မှတ်တမ်းတင်ရန်',
    'agri.water.editTitle': 'မှတ်တမ်းပြင်ဆင်ရန်',
    'agri.water.confirmDelete': 'ဤမှတ်တမ်းကို ဖျက်မည်လား?',
    'agri.water.noData': 'ဒေတာမရှိသေးပါ',
    'agri.water.colUsage': 'ပမာဏ (L)',
    'agri.water.colSource': 'ရေရင်းမြစ်',
    'agri.water.colCost': 'ကုန်ကျစရိတ်',
    'agri.water.fieldUsage': 'ပမာဏ (လီတာ)',
    'agri.water.fieldCost': 'ကုန်ကျစရိတ် (฿)',
    'agri.water.source.tap': 'ပိုက်ရေ',
    'agri.water.source.well': 'တွင်းရေ',
    'agri.water.source.rain': 'မိုးရေ',
    'agri.water.source.river': 'သဘာဝရေရင်းမြစ်',

    'agri.yield.gradeA': 'အဆင့် A',
    'agri.yield.gradeB': 'အဆင့် B',
    'agri.yield.gradeC': 'အဆင့် C',
    'agri.yield.gradeReject': 'ပယ်ဖျက်',
    'agri.yield.statHarvestCount': 'ရိတ်သိမ်းအကြိမ်',
    'agri.yield.statTotalYield': 'စုစုပေါင်းအထွက် (kg)',
    'agri.yield.statTotalRevenue': 'စုစုပေါင်းဝင်ငွေ',
    'agri.yield.statAvgPrice': 'ပျမ်းမျှစျေးနှုန်း/kg',
    'agri.yield.chartTitle': 'လစဉ်အထွက်နှုန်း',
    'agri.yield.chartQuantity': 'ပမာဏ (kg)',
    'agri.yield.chartRevenue': 'ဝင်ငွေ (฿)',
    'agri.yield.addRecord': 'အထွက်မှတ်တမ်းတင်ရန်',
    'agri.yield.addTitle': 'အထွက်မှတ်တမ်းတင်ရန်',
    'agri.yield.editTitle': 'မှတ်တမ်းပြင်ဆင်ရန်',
    'agri.yield.confirmDelete': 'ဤမှတ်တမ်းကို ဖျက်မည်လား?',
    'agri.yield.noData': 'ဒေတာမရှိသေးပါ',
    'agri.yield.colCrop': 'သီးနှံ',
    'agri.yield.colQuantity': 'ပမာဏ',
    'agri.yield.colGrade': 'အဆင့်',
    'agri.yield.colPricePerUnit': 'တစ်ယူနစ်စျေး',
    'agri.yield.colRevenue': 'ဝင်ငွေ',
    'agri.yield.fieldHarvestDate': 'ရိတ်သိမ်းသည့်နေ့',
    'agri.yield.fieldPricePerUnit': 'တစ်ယူနစ်စျေးနှုန်း (฿)',
    'agri.yield.totalRevenue': 'စုစုပေါင်းဝင်ငွေ',
    'agri.yield.unitKg': 'ကီလိုဂရမ် (kg)',
    'agri.yield.unitG': 'ဂရမ် (g)',
    'agri.yield.unitPcs': 'ခု',
    'agri.yield.unitBunch': 'စည်း',

    'soil.airSection': 'လေအချက်အလက်',
    'soil.soilSection': 'မြေအချက်အလက် (၁၀ နေရာ)',
    'soil.systemSection': 'စနစ်အချက်အလက်',

    'charts.range1h': '၁ နာရီ',
    'charts.range6h': '၆ နာရီ',
    'charts.range24h': '၂၄ နာရီ',
    'charts.range7d': '၇ ရက်',
    'charts.range30d': '၃၀ ရက်',
    'charts.rangeCustom': 'သတ်မှတ်ရန်',
    'charts.soilMoisture': 'စိုထိုင်းဆ',
    'charts.soilTemp': 'အပူချိန်',
    'charts.errSelectDate': 'စတင်နှင့် ဆုံးသည့်နေ့ ရွေးပါ',
    'charts.errStartBeforeEnd': 'စတင်နေ့ သည် ဆုံးသည့်နေ့ ထက် ကြိုဖြစ်ရမည်',
    'charts.errMax90Days': 'အများဆုံး ၉၀ ရက် ရွေးနိုင်သည်',
    'charts.errSelectRange': 'အချိန်ကာလ ရွေးပါ',
    'charts.loadError': 'ဂရပ်ဒေတာ မဖွင့်နိုင်ပါ',
    'charts.notReady': 'ဤဖန်လုံအိမ် ဂရပ်ပြရန် အဆင်မသင့်သေးပါ',
    'charts.airChartTitle': 'လေအချက်အလက် ဂရပ်',
    'charts.soilChartTitle': 'မြေအချက်အလက် ဂရပ်',
    'charts.fullscreen': 'မျက်နှာပြင်အပြည့်',
    'charts.allNodes': 'နေရာ ၁၀ ခုလုံး',
    'charts.selectNode': 'နေရာရွေးပါ',
    'charts.show': 'ပြရန်',
    'charts.params': 'ကိန်းရှင်များ',
    'charts.nodeLabel': 'နေရာ',
    'charts.node': 'နေရာ',
    'charts.timeRange': 'အချိန်ကာလ',
    'charts.customRangeTitle': 'အချိန်ကာလ သတ်မှတ်ရန် (အများဆုံး ၉၀ ရက်)',
    'charts.startDate': 'စတင်နေ့',
    'charts.endDate': 'ဆုံးသည့်နေ့',
    'charts.showingData': 'ဒေတာပြသနေသည်',
    'charts.to': 'မှ',

    'timers.fan1': 'ပန်ကာကြီး',
    'timers.fan2': 'လေကြောင်းလှည့်ပန်ကာ',
    'timers.valve1': 'ရေဇုန် ၁',
    'timers.valve2': 'ရေဇုန် ၂',
    'timers.valve3': 'ရေဇုန် ၃',
    'timers.valve4': 'ရေဇုန် ၄',
    'timers.light1': 'ဖြည့်စွက်မီး',
    'timers.motor': 'မော်တာ (အားလုံး)',
    'timers.saveSuccess': 'အချိန်သိမ်းဆည်းပြီးပါပြီ',
    'timers.invalidFormat': 'အချိန်ပုံစံမမှန်ပါ (HH:mm)',
    'timers.noPermission': 'အချိန်သတ်မှတ်ခွင့်မရှိပါ (superadmin / operator / admin လိုသည်)',
    'timers.infoNote': '⏰ အလိုအလျောက် ဖွင့်/ပိတ် အချိန်ဇယား (ထိန်းချုပ်တက်တွင် Auto မုဒ်ဖွင့်ရမည်)',
    'timers.onTime': 'ဖွင့်ချိန်',
    'timers.offTime': 'ပိတ်ချိန်',
    'timers.motorDesc': 'ရွေ့လျားမှုအချိန် (Forward/Reverse)',
    'timers.forwardTime': 'Forward အချိန် (ဆင်း)',
    'timers.reverseTime': 'Reverse အချိန် (တက်)',

    'auto.water': 'ရေဖွင့် (ဇုန် ၄ ခု)',
    'auto.motorShade': 'အရိပ်ကာစနစ် (ဇုန် ၄ ခု)',
    'auto.sensorAirTemp': 'လေအပူချိန်',
    'auto.sensorAirHumidity': 'လေစိုထိုင်းဆ',
    'auto.sensorLight': 'အလင်း',
    'auto.sensorCo2': 'CO2',
    'auto.sensorSoilMoisture': 'မြေစိုထိုင်းဆ နေရာ {n}',
    'auto.condGt': 'ကျော်လွန် (>)',
    'auto.condLt': 'နည်း (<)',
    'auto.condGte': 'ကျော်လွန်သည် သို့မဟုတ် တူ (≥)',
    'auto.condLte': 'နည်းသည် သို့မဟုတ် တူ (≤)',
    'auto.modeDaily': 'နေ့စဉ်အချိန်ဇယား',
    'auto.modeCondition': 'အခြေအနေအရ',
    'auto.modeCycle': 'ပတ်လည်လုပ်ဆောင်မှု',
    'auto.title': 'အလိုအလျောက်စနစ်',
    'auto.subtitle': 'စမတ်စနစ် ၃ ခုဖြင့် အလိုအလျောက်ထိန်းချုပ်ရန်',
    'auto.overview': 'ခြုံငုံသုံးသပ်ချက်',
    'auto.configMode': 'Auto မုဒ်သတ်မှတ်ရန်',
    'auto.cardDaily': 'ဖွင့်/ပိတ်အချိန်ဇယား',
    'auto.cardCondition': 'အခြေအနေအရ ထိန်းချုပ်မှု',
    'auto.cardCycle': 'ပတ်လည်လုပ်ဆောင်မှု',
    'auto.realtimeStatus': 'Real-time အခြေအနေ',
    'auto.updating': 'အပ်ဒိတ်လုပ်နေသည်...',
    'auto.working': 'လုပ်ဆောင်နေသည်',
    'auto.idle': 'အနားယူနေသည်',
    'auto.controlSystem': 'ထိန်းချုပ်မှုစနစ်',
    'auto.deviceOffline': 'စက်ပစ္စည်း အော့ဖ်လိုင်း',
    'auto.offlineDesc': 'အလိုအလျောက်စနစ်သည် စက်ထဲသိမ်းဆည်းထားသောနောက်ဆုံးဆက်တင်အတိုင်း လုပ်ဆောင်မည်',

    'air.staleData': 'ဒေတာမအပ်ဒိတ်ပါ',
    'air.outOfRange': 'တန်ဖိုးပုံမမှန်ပါ',
    'air.temperature': 'အပူချိန်',
    'air.tempWarning': 'အပူချိန်ပုံမမှန်ပါ',
    'air.humidity': 'စိုထိုင်းဆ',
    'air.humidityWarning': 'စိုထိုင်းဆပုံမမှန်ပါ',
    'air.co2Warning': 'CO₂ များလွန်းသည်',
    'air.light': 'အလင်း',

    'autoDevice.sequential': 'ဇုန် ၄ ခု လှည့်ပတ်စနစ်',
    'autoDevice.allZones': 'ဇုန် ၄ ခု တပြိုင်နက်',
    'autoDevice.autoControl': 'အလိုအလျောက်ထိန်းချုပ်',
    'autoDevice.currentMode': 'လက်ရှိမုဒ်',
    'autoDevice.unknown': 'မသိပါ',
    'autoDevice.tabSchedule': 'အချိန်ဇယား',
    'autoDevice.tabCondition': 'အခြေအနေအရ',
    'autoDevice.tabCycle': 'ပတ်လည်',
    'autoDevice.dailyInfo': 'ရိုးရှင်းသောဖွင့်/ပိတ်အချိန်ဇယား၊ နေ့စဉ်ထပ်ခါ',
    'autoDevice.dailyGoTo': '"အချိန်ဇယား" တက်တွင် သတ်မှတ်ပါ',
    'autoDevice.enable': 'ဖွင့်ရန်',
    'autoDevice.conditionDesc': 'Sensor အခြေအနေအရ ထိန်းချုပ်ရန်',
    'autoDevice.selectSensor': 'Sensor ရွေးပါ',
    'autoDevice.condition': 'အခြေအနေ',
    'autoDevice.targetValue': 'ရည်မှန်းတန်ဖိုး',
    'autoDevice.whenTrue': 'အခြေအနေမှန်သောအခါ',
    'autoDevice.actionForward': 'ရှေ့တိုး',
    'autoDevice.actionReverse': 'နောက်ဆုတ်',
    'autoDevice.actionOn': 'ဖွင့်',
    'autoDevice.actionOff': 'ပိတ်',
    'autoDevice.exampleTitle': 'ဥပမာ',
    'autoDevice.saving': 'သိမ်းဆည်းနေသည်...',
    'autoDevice.saveBtn': 'ဆက်တင်သိမ်းဆည်းရန်',
    'autoDevice.cycleDesc': 'ပတ်လည်လုပ်ဆောင်မှု',
    'autoDevice.timeRange': 'လုပ်ဆောင်သည့်အချိန်ကာလ',
    'autoDevice.start': 'စတင်',
    'autoDevice.end': 'ဆုံးသည်',
    'autoDevice.onTimePerZone': 'တစ်ဇုန်လျှင်အချိန် (မိနစ်)',
    'autoDevice.onTime': 'ဖွင့်ချိန် (မိနစ်)',
    'autoDevice.waitBetween': 'ပတ်ကြားစောင့်ချိန် (မိနစ်)',
    'autoDevice.offTime': 'ပိတ်ချိန် (မိနစ်)',
    'autoDevice.maxCycles': 'အများဆုံးပတ်ရေ',
    'autoDevice.maxCyclesHint': '၁-၁၀ ပတ် ဖြည့်နိုင်သည်',
    'autoDevice.exampleCycle1': 'ဥပမာ (ပတ် ၁):',
    'autoDevice.zone1': 'ရေဇုန် ၁ ဖွင့်',
    'autoDevice.zone2': 'ရေဇုန် ၂ ဖွင့်',
    'autoDevice.zone3': 'ရေဇုန် ၃ ဖွင့်',
    'autoDevice.zone4': 'ရေဇုန် ၄ ဖွင့်',
    'autoDevice.cycleWait': '{off} မိနစ်စောင့်၍ ထပ်ခါ (အများဆုံး {max} ပတ်)',
    'autoDevice.cycleWarning': '⚠️ အချိန်နီးလာပါက ဇုန် ၄ ခုပြီးမှ ရပ်မည်',
    'autoDevice.cycleExample': 'အချိန် {start}-{end} → ဖွင့် {on} မိနစ်၊ ပိတ် {off} မိနစ်၊ {max} ပတ်',

    'autoMode.title': 'အချိန်ဇယားအလိုအလျောက်မုဒ်',
    'autoMode.subtitle': 'အချိန်ဇယားအလိုအလျောက်ထိန်းချုပ်မှု ဖွင့်/ပိတ်',
    'autoMode.infoText': 'အလိုအလျောက်မုဒ်ဖွင့်ထားလျှင် သတ်မှတ်ထားသောအချိန်ဇယားအတိုင်း အလုပ်လုပ်မည်',
    'autoMode.on': 'ဖွင့်',
    'autoMode.off': 'ပိတ်',
    'autoMode.fan1': 'ပန်ကာကြီး',
    'autoMode.fan2': 'လေကြောင်းလှည့်ပန်ကာ',
    'autoMode.valve1': 'ရေဇုန် ၁',
    'autoMode.valve2': 'ရေဇုန် ၂',
    'autoMode.valve3': 'ရေဇုန် ၃',
    'autoMode.valve4': 'ရေဇုန် ၄',
    'autoMode.light1': 'ဖြည့်စွက်မီး',
    'autoMode.motor': 'မော်တာအားလုံး',

    'banner.developingTitle': 'တည်ဆောက်နေဆဲ',
    'banner.developingDefault': 'ThingsBoard Device (deviceId) မချိတ်ဆက်ရသေးပါ',
    'banner.developingNote': 'တန်ဖိုးအားလုံး -- ပြမည်၊ ထိန်းချုပ်မှုအားလုံး သော့ခတ်မည်',
    'banner.offlineTitle': 'စက်ပစ္စည်း အော့ဖ်လိုင်း',
    'banner.offlineDesc': 'စက်ပစ္စည်းနှင့် ချိတ်ဆက်မရပါ၊ ထိန်းချုပ်မှုအားလုံး သော့ခတ်မည်',

    'export.title': 'ဒေတာထုတ်ယူရန်',
    'export.format': 'ဖိုင်ပုံစံ',
    'export.dateRange': 'ရက်စွဲကာလ',
    'export.last7': 'နောက်ဆုံး ၇ ရက်',
    'export.last30': 'နောက်ဆုံး ၃၀ ရက်',
    'export.last90': 'နောက်ဆုံး ၉၀ ရက်',
    'export.startDate': 'စတင်နေ့',
    'export.endDate': 'ဆုံးသည့်နေ့',
    'export.maxNote': '* အများဆုံး ၁ နှစ် ရွေးနိုင်သည်',
    'export.airSensors': 'လေ Sensor များ',
    'export.soilNodes': 'မြေနေရာများ',
    'export.soilNode': 'နေရာ {n}',
    'export.soilParams': 'မြေကိန်းရှင်များ',
    'export.downloadBtn': 'ဒေါင်းလုဒ်',
    'export.downloading': 'ဒေါင်းလုဒ်လုပ်နေသည်...',
    'export.btnLabel': 'Export',
    'export.successMsg': 'ဒေါင်းလုဒ်အောင်မြင်သည် ({n} ရက်)',
    'export.errNoDate': 'စတင်နှင့် ဆုံးသည့်နေ့ ရွေးပါ',
    'export.errStartEnd': 'စတင်နေ့သည် ဆုံးသည့်နေ့ထက် ကြိုဖြစ်ရမည်',
    'export.errMax1Year': 'အများဆုံး ၁ နှစ် Export လုပ်နိုင်သည်',
    'export.errNoSensor': 'Sensor အနည်းဆုံး ၁ ခုရွေးပါ',
    'export.errFailed': 'Export မရပါ',

    'exportSimple.selectRange': 'ကာလရွေးပါ',
    'exportSimple.days': '{n} ရက်',
    'exportSimple.successMsg': '{n} ရက် ဒေတာ ဒေါင်းလုဒ်အောင်မြင်သည်',

    'motor.notReady': 'မအဆင်သင့်ပါ',
    'motor.shading': '🌥️ အရိပ်ကာနေသည်',
    'motor.notShading': '☀️ အရိပ်မကာပါ',
    'motor.stopped': '⏸️ ရပ်နေသည်',
    'motor.shade': 'အရိပ်ကာရန်',
    'motor.unshade': 'အရိပ်ဖယ်ရန်',

    'relay.working': 'လုပ်ဆောင်နေသည်',
    'relay.idle': 'ရပ်နေသည်',

    'report.title': 'PDF အစီရင်ခံစာ ဒေါင်းလုဒ်',
    'report.subtitle': 'Sensor ဒေတာနှင့် စာရင်းအင်းများ',
    'report.period1d': 'နေ့စဉ်',
    'report.period1dDesc': 'နောက်ဆုံး ၂၄ နာရီ',
    'report.period7d': 'အပတ်စဉ်',
    'report.period7dDesc': 'နောက်ဆုံး ၇ ရက်',
    'report.period30d': 'လစဉ်',
    'report.period30dDesc': 'နောက်ဆုံး ၃၀ ရက်',
    'report.shareTitle': 'အစီရင်ခံစာ သိမ်းဆည်း/မျှဝေရန်',
    'report.successMsg': 'အစီရင်ခံစာ ဒေါင်းလုဒ်အောင်မြင်သည်',
    'report.errFailed': 'အစီရင်ခံစာ ဒေါင်းလုဒ်မရပါ',
    'report.downloadBtn': 'အစီရင်ခံစာ ဒေါင်းလုဒ်',

    'soil.nutrients': 'အာဟာရဓာတ် (mg/kg)',

    'sysinfo.signal': 'အချက်ပြ',
    'sysinfo.readTime': 'ဒေတာဖတ်ချိန်',
    'sysinfo.sigUnknown': 'မသိပါ',
    'sysinfo.sigExcellent': 'အလွန်ကောင်း',
    'sysinfo.sigGood': 'ကောင်း',
    'sysinfo.sigFair': 'သင့်တော်သည်',
    'sysinfo.sigWeak': 'အားနည်း',
    'sysinfo.sigVeryWeak': 'အလွန်အားနည်း',

    'weather.unavailable': 'အပြင်ရာသီဥတုဒေတာမဖွင့်နိုင်ပါ',
    'weather.updated': 'အပ်ဒိတ်',
    'weather.data_at': 'ဒေတာ ရရှိချိန်',
    'weather.cached': 'cache',
    'weather.sectionTitle': 'အပြင်ရာသီဥတု',
    'admin.weather': 'ရာသီဥတုဆက်တင်',
    'admin.weather.subtitle': 'ဖန်လုံအိမ်တစ်ခုချင်းအတွက် တည်နေရာနှင့် ပြသမည့်ဒေတာ သတ်မှတ်ရန်',
    'admin.weather.searchPlaceholder': 'တည်နေရာရှာရန်...',
    'admin.weather.searchBtn': 'ရှာရန်',
    'admin.weather.selectLocation': 'တည်နေရာရွေးပါ',
    'admin.weather.currentLocation': 'လက်ရှိတည်နေရာ',
    'admin.weather.latitude': 'Latitude',
    'admin.weather.longitude': 'Longitude',
    'admin.weather.showFields': 'ပြသမည့်ဒေတာ',
    'admin.weather.showTemperature': 'အပူချိန်',
    'admin.weather.showHumidity': 'စိုထိုင်းဆ',
    'admin.weather.showCondition': 'ရာသီဥတုအခြေအနေ',
    'admin.weather.showWindSpeed': 'လေမြန်နှုန်း',
    'admin.weather.showUvIndex': 'UV ညွှန်းကိန်း',
    'admin.weather.showRainChance': 'မိုးရွာနိုင်ခြေ',
    'admin.weather.saveSuccess': 'သိမ်းဆည်းပြီးပါပြီ',
    'admin.weather.noConfig': 'ဆက်တင်မရှိသေးပါ (ပုံသေ: Bangkok)',
    'admin.calibrate': 'Sensor ကိုချိန်ညှိရန်',

  },
};

export function t(key: string, lang: Language = 'th'): string {
  return translations[lang]?.[key] || translations['th']?.[key] || key;
}

export function useTranslation(lang: Language = 'th') {
  const translate = useCallback((key: string) => t(key, lang), [lang]);
  return { t: translate, lang };
}

export function useT() {
  const { language } = useAppSettings();
  const translate = useCallback((key: string) => t(key, language), [language]);
  return { t: translate, lang: language };
}